// ============================================================================
// API CLIENT FOR ISPOT BACKEND
// ============================================================================

import type {
  AIInterviewStreamEvent,
  JourneyFramework,
  ConfigurableItemsResponse,
  UpdatePhaseConfigRequest,
  DocTemplateResponse,
  DocTemplatesListResponse,
  DocTemplateGetResponse,
  CreateDocTemplateRequest,
  UpdateDocTemplateConfigRequest,
  DocConfigurableItemsResponse,
  SectionTemplate,
  SectionTemplatesListResponse,
  CreateSectionTemplateRequest,
  WorkspaceActivity,
  WorkspaceDocBlock,
  DraftSuggestion,
} from '@ispot/shared'

// Always use relative URLs - proxied through Vite (dev) or Caddy (prod)
export const API_URL = import.meta.env.VITE_API_URL ?? ''
const TOKEN_KEY = 'ispot-session-token'

// Token management for cross-origin auth
export function getSessionToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setSessionToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearSessionToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export interface ApiError {
  error: string
  message: string
}

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string
  isSuperAdmin?: boolean
}

export interface Account {
  id: string
  name: string
  slug: string
  role: 'owner' | 'admin' | 'member'
}

export type ExtractionMode = 'default' | 'hybrid' | 'optimized'

export type CompanyType = 'concept' | 'startup' | 'scale_up' | 'grown_up' | 'enterprise'
export type ProductType = 'software' | 'hardware' | 'service' | 'solution' | 'platform' | 'marketplace'
export type MarketType = 'b2c' | 'b2b' | 'b2b2c' | 'b2g' | 'd2c'
export type ProjectStatus = 'active' | 'paused' | 'archived'
export type TargetRegion = 'global' | 'eu' | 'benelux'

export interface Project {
  id: string
  name: string
  slug: string
  description?: string | null
  graphUri: string
  visualAnalysisEnabled?: boolean
  extractionMode?: ExtractionMode
  optimizedExtractionTemperature?: number
  companyType?: CompanyType | null
  productType?: ProductType | null
  market?: MarketType | null
  industry?: string | null
  targetRegion?: TargetRegion | null
  status?: ProjectStatus
  tags?: string[]
  clientName?: string | null
  role: string
  createdAt: string
  updatedAt?: string
}

export interface ProjectStats {
  personas: number
  competitors: number
  documents: number
  interviews: number
  lastActivityAt: string | null
  currentPhaseId: string | null
  phaseProgress: number
}

export interface AccountSummary {
  total: number
  byCompanyType: Record<string, number>
  byMarket: Record<string, number>
  byProductType: Record<string, number>
  byStatus: Record<string, number>
  byPhase: Record<string, number>
  totalPersonas: number
  totalDocuments: number
}

export interface ProjectMetadataPatch {
  name?: string
  description?: string | null
  companyType?: CompanyType | null
  productType?: ProductType | null
  market?: MarketType | null
  industry?: string | null
  targetRegion?: TargetRegion | null
  status?: ProjectStatus
  tags?: string[]
  clientName?: string | null
  visualAnalysisEnabled?: boolean
  extractionMode?: ExtractionMode
  optimizedExtractionTemperature?: number
}

export interface ProjectMember {
  user: {
    id: string
    email: string
    name: string
    avatarUrl?: string
  }
  role: string
  joinedAt: string
}

export interface RoleDefinition {
  key: string
  name: string
  description: string
  permissions: string[]
}

// Fetch wrapper with token auth and error handling
async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  if (typeof window !== 'undefined' && path.includes('customer-segments')) {
    // TEMP loop probe — remove after diagnosing the Fit Thesis flood
    // eslint-disable-next-line no-console
    console.trace('[apiFetch loop probe]', path)
  }
  const token = getSessionToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  })

  const data = await response.json()

  if (!response.ok) {
    const error = data as ApiError
    // On 401, clear session and persisted auth state to stop query loops
    if (response.status === 401) {
      clearSessionToken()
      // Clear persisted Zustand stores to prevent stale auth state
      localStorage.removeItem('ispot-auth-v3')
      localStorage.removeItem('ispot-project-v3')
    }
    throw new Error(error.error || error.message || `API error: ${response.status}`)
  }

  return data as T
}

// ============================================================================
// GENERIC API CLIENT
// ============================================================================

export const apiClient = {
  async get<T = unknown>(path: string): Promise<{ data: T }> {
    const data = await apiFetch<T>(path)
    return { data }
  },

  async post<T = unknown>(path: string, body?: unknown): Promise<{ data: T }> {
    const data = await apiFetch<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
    return { data }
  },

  async put<T = unknown>(path: string, body?: unknown): Promise<{ data: T }> {
    const data = await apiFetch<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
    return { data }
  },

  async patch<T = unknown>(path: string, body?: unknown): Promise<{ data: T }> {
    const data = await apiFetch<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
    return { data }
  },

  async delete<T = unknown>(path: string): Promise<{ data: T }> {
    const data = await apiFetch<T>(path, { method: 'DELETE' })
    return { data }
  },
}

// ============================================================================
// AUTH API
// ============================================================================

export const authApi = {
  async register(email: string, password: string, name: string, companyName: string): Promise<{ user: User; account: Account }> {
    const result = await apiFetch<{ user: User; account: Account; sessionId: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, companyName }),
    })
    // Store session token for cross-origin auth
    setSessionToken(result.sessionId)
    return { user: result.user, account: { ...result.account, role: 'owner' } }
  },

  async login(email: string, password: string): Promise<User> {
    const result = await apiFetch<{ user: User; sessionId: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    // Store session token for cross-origin auth
    setSessionToken(result.sessionId)
    return result.user
  },

  async logout(): Promise<void> {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' })
    } finally {
      clearSessionToken()
    }
  },

  async me(): Promise<{ user: User; accounts: Account[] } | null> {
    try {
      const result = await apiFetch<{ user: User; accounts: Account[] }>('/api/auth/me')
      return result
    } catch {
      clearSessionToken()
      return null
    }
  },
}

// ============================================================================
// PROJECTS API
// ============================================================================

export const projectsApi = {
  async list(accountId: string): Promise<Project[]> {
    const result = await apiFetch<{ projects: Project[] }>(`/api/accounts/${accountId}/projects`)
    return result.projects
  },

  async portfolio(accountId: string): Promise<{
    projects: Project[]
    stats: Record<string, ProjectStats>
    summary: AccountSummary
  }> {
    return apiFetch<{
      projects: Project[]
      stats: Record<string, ProjectStats>
      summary: AccountSummary
    }>(`/api/accounts/${accountId}/projects/portfolio`)
  },

  async create(
    accountId: string,
    payload: {
      name: string
      slug: string
      description?: string
      companyType?: CompanyType | null
      productType?: ProductType | null
      market?: MarketType | null
      industry?: string | null
      clientName?: string | null
      tags?: string[]
    }
  ): Promise<Project> {
    const result = await apiFetch<{ project: Project }>(`/api/accounts/${accountId}/projects`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return result.project
  },

  async get(accountId: string, projectId: string): Promise<Project> {
    const result = await apiFetch<{ project: Project }>(`/api/accounts/${accountId}/projects/${projectId}`)
    return result.project
  },

  async update(
    accountId: string,
    projectId: string,
    updates: ProjectMetadataPatch
  ): Promise<Project> {
    const result = await apiFetch<{ project: Project }>(`/api/accounts/${accountId}/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
    return result.project
  },

  async delete(accountId: string, projectId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}`, { method: 'DELETE' })
  },

  async getMembers(accountId: string, projectId: string): Promise<ProjectMember[]> {
    const result = await apiFetch<{ members: ProjectMember[] }>(`/api/accounts/${accountId}/projects/${projectId}/members`)
    return result.members
  },

  async addMember(accountId: string, projectId: string, email: string, role: string): Promise<ProjectMember> {
    const result = await apiFetch<{ member: ProjectMember }>(`/api/accounts/${accountId}/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    })
    return result.member
  },

  async updateMember(accountId: string, projectId: string, userId: string, role: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    })
  },

  async removeMember(accountId: string, projectId: string, userId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/members/${userId}`, { method: 'DELETE' })
  },

  async getRoles(accountId: string, projectId: string): Promise<RoleDefinition[]> {
    const result = await apiFetch<{ roles: RoleDefinition[] }>(`/api/accounts/${accountId}/projects/${projectId}/roles`)
    return result.roles
  },
}

// ============================================================================
// CRM TYPES
// ============================================================================

export interface Person {
  id: string
  projectId: string
  firstName: string
  lastName: string
  email?: string | null
  phone?: string | null
  jobTitle?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
}

export interface Customer {
  id: string
  projectId: string
  customerName: string
  customerType?: 'prospect' | 'active' | 'churned' | 'partner' | null
  billingAddress?: string | null
  city?: string | null
  country?: string | null
  industry?: string | null
  website?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
}

export interface CustomerContact {
  person: Person
  role?: 'primary' | 'billing' | 'technical' | 'decision_maker' | null
  isPrimary: boolean
}

// ============================================================================
// CRM API - PERSONS (CONTACTS)
// ============================================================================

export const personsApi = {
  async list(accountId: string, projectId: string): Promise<Person[]> {
    const result = await apiFetch<{ persons: Person[] }>(`/api/accounts/${accountId}/projects/${projectId}/persons`)
    return result.persons
  },

  async get(accountId: string, projectId: string, personId: string): Promise<Person> {
    const result = await apiFetch<{ person: Person }>(`/api/accounts/${accountId}/projects/${projectId}/persons/${personId}`)
    return result.person
  },

  async create(accountId: string, projectId: string, data: {
    firstName: string
    lastName: string
    email?: string | null
    phone?: string | null
    jobTitle?: string | null
    notes?: string | null
  }): Promise<Person> {
    const result = await apiFetch<{ person: Person }>(`/api/accounts/${accountId}/projects/${projectId}/persons`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result.person
  },

  async update(accountId: string, projectId: string, personId: string, data: {
    firstName?: string
    lastName?: string
    email?: string | null
    phone?: string | null
    jobTitle?: string | null
    notes?: string | null
  }): Promise<Person> {
    const result = await apiFetch<{ person: Person }>(`/api/accounts/${accountId}/projects/${projectId}/persons/${personId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return result.person
  },

  async delete(accountId: string, projectId: string, personId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/persons/${personId}`, { method: 'DELETE' })
  },
}

// ============================================================================
// CRM API - CUSTOMERS
// ============================================================================

export const customersApi = {
  async list(accountId: string, projectId: string): Promise<Customer[]> {
    const result = await apiFetch<{ customers: Customer[] }>(`/api/accounts/${accountId}/projects/${projectId}/customers`)
    return result.customers
  },

  async get(accountId: string, projectId: string, customerId: string): Promise<{ customer: Customer; contacts: CustomerContact[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}`)
  },

  async create(accountId: string, projectId: string, data: {
    customerName: string
    customerType?: 'prospect' | 'active' | 'churned' | 'partner' | null
    billingAddress?: string | null
    city?: string | null
    country?: string | null
    industry?: string | null
    website?: string | null
    notes?: string | null
  }): Promise<Customer> {
    const result = await apiFetch<{ customer: Customer }>(`/api/accounts/${accountId}/projects/${projectId}/customers`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result.customer
  },

  async update(accountId: string, projectId: string, customerId: string, data: {
    customerName?: string
    customerType?: 'prospect' | 'active' | 'churned' | 'partner' | null
    billingAddress?: string | null
    city?: string | null
    country?: string | null
    industry?: string | null
    website?: string | null
    notes?: string | null
  }): Promise<Customer> {
    const result = await apiFetch<{ customer: Customer }>(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return result.customer
  },

  async delete(accountId: string, projectId: string, customerId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}`, { method: 'DELETE' })
  },

  async addContact(accountId: string, projectId: string, customerId: string, data: {
    personId: string
    role?: 'primary' | 'billing' | 'technical' | 'decision_maker' | null
    isPrimary?: boolean
  }): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async removeContact(accountId: string, projectId: string, customerId: string, personId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}/contacts/${personId}`, { method: 'DELETE' })
  },
}

// ============================================================================
// SOURCES API (Generic Document Analyzer)
// ============================================================================

export type ContentType =
  | 'user_interview' | 'buyer_interview' | 'stakeholder_meeting'
  | 'team_meeting' | 'vision_document' | 'requirements_spec'
  | 'presentation' | 'research_notes' | 'competitor_analysis'
  | 'support_ticket' | 'survey_response' | 'website' | 'transcript' | 'custom'

export type SourceStatus =
  | 'uploading'
  | 'parsing'
  | 'analyzing'
  | 'detecting-entities'
  | 'ready-for-review'
  | 'completed'
  | 'error'
  // Legacy statuses
  | 'pending'
  | 'analyzed'

export type FacetStatus = 'pending' | 'accepted' | 'rejected' | 'mapped'

export interface Source {
  id: string
  projectId: string
  accountId: string
  createdById: string
  title: string
  contentType: ContentType
  rawContent: string
  markdownContent: string
  fileType?: string | null
  fileName?: string | null
  status: SourceStatus
  analyzedAt?: string | null
  analysisError?: string | null
  metadata?: Record<string, unknown>
  tags?: string[]
  createdAt: string
  updatedAt: string
}

export interface SourceWithStats extends Source {
  facetStats: {
    total: number
    pending: number
    accepted: number
    rejected: number
    mapped: number
  }
}

export interface DetectedFacet {
  id: string
  sourceId: string
  projectId: string
  facetTypeId: string
  title?: string | null
  content: string
  evidence: string | null
  confidence: number

  // Discriminator: 'text' or 'visual'
  sourceType?: 'text' | 'visual'

  // Text-specific position fields
  positionStart?: number | null
  positionEnd?: number | null

  // Visual-specific fields
  imageId?: string | null
  pageNumber?: number | null
  bboxX?: number | null
  bboxY?: number | null
  bboxWidth?: number | null
  bboxHeight?: number | null

  // Problem statement (Three C's)
  problemStatement?: {
    context: string
    cause: string
    consequence: string
  } | null

  suggestedEntityType?: string | null
  suggestedReason?: string | null
  status: FacetStatus
  mappedEntityType?: string | null
  mappedEntityUri?: string | null
  decidedAt?: string | null
  decidedById?: string | null
  graphUri?: string | null
  createdAt: string
}

export interface SourceWithFacets extends Source {
  facets: DetectedFacet[]
  facetStats: {
    total: number
    pending: number
    accepted: number
    rejected: number
    mapped: number
  }
}

// ============================================================================
// VISUAL ANALYSIS TYPES
// ============================================================================

export type ImageType =
  | 'logo' | 'business_canvas' | 'whiteboard' | 'architecture_diagram'
  | 'process_diagram' | 'ui_screenshot' | 'wireframe' | 'chart'
  | 'org_chart' | 'journey_map' | 'swot' | 'mindmap'
  | 'timeline' | 'table' | 'photo' | 'unknown'

export interface SourceImage {
  id: string
  sourceId: string
  projectId: string
  imageData?: string | null  // Base64 encoded
  thumbnailData?: string | null
  mimeType: string
  pageNumber: number
  bboxX?: number | null
  bboxY?: number | null
  bboxWidth?: number | null
  bboxHeight?: number | null
  imageType: ImageType
  classificationConfidence?: number | null
  analyzed: boolean
  analysisError?: string | null
  createdAt: string
}

export interface VisualFacet {
  id: string
  sourceId: string
  projectId: string
  imageId: string
  documentEntityId?: string | null
  facetType: string
  title: string
  content?: string | null
  evidence?: string | null
  confidence: number
  bboxX?: number | null
  bboxY?: number | null
  bboxWidth?: number | null
  bboxHeight?: number | null
  suggestedEntityType?: string | null
  suggestedReason?: string | null
  status: FacetStatus
  mappedEntityType?: string | null
  mappedEntityUri?: string | null
  decidedAt?: string | null
  decidedById?: string | null
  createdAt: string
}

// ============================================================================
// FACET ENTITY GROUPS (Many-to-One Mapping)
// ============================================================================

export type GroupStatus = 'draft' | 'confirmed' | 'created'
export type FacetRole = 'primary' | 'evidence' | 'variant'

export interface FacetEntityGroup {
  id: string
  projectId: string
  sourceId: string
  createdById?: string | null
  entityLabel: string
  entityType: string
  entityUri?: string | null
  entityDescription?: string | null
  aiSuggested: boolean
  aiConfidence?: number | null
  aiReason?: string | null
  status: GroupStatus
  createdAt: string
  updatedAt: string
}

export interface FacetGroupMember {
  groupId: string
  facetId: string
  role: FacetRole
  addedAt: string
  facet?: DetectedFacet
}

export interface FacetEntityGroupWithMembers extends FacetEntityGroup {
  members: FacetGroupMember[]
}

export interface EntityGroupWithFacets extends FacetEntityGroup {
  facets: Array<DetectedFacet & { role: FacetRole }>
}

export interface FacetConfiguration {
  id: string
  projectId: string
  facetTypeId: string
  isEnabled: boolean
  customEntityType?: string | null
  updatedAt: string
}

export const sourcesApi = {
  async list(accountId: string, projectId: string): Promise<SourceWithStats[]> {
    const result = await apiFetch<{ sources: SourceWithStats[] }>(`/api/accounts/${accountId}/projects/${projectId}/sources`)
    return result.sources
  },

  async get(accountId: string, projectId: string, sourceId: string): Promise<SourceWithFacets> {
    const result = await apiFetch<{ source: SourceWithFacets }>(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}`)
    return result.source
  },

  async create(accountId: string, projectId: string, data: {
    title: string
    contentType: ContentType
    rawContent: string
    markdownContent?: string
    fileName?: string
    fileType?: 'pdf' | 'md' | 'txt'
    fileData?: string // Base64 encoded file binary
    tags?: string[]
    metadata?: Record<string, unknown>
  }): Promise<Source> {
    const result = await apiFetch<{ source: Source }>(`/api/accounts/${accountId}/projects/${projectId}/sources`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result.source
  },

  async update(accountId: string, projectId: string, sourceId: string, data: {
    title?: string
    contentType?: ContentType
    tags?: string[]
    metadata?: Record<string, unknown>
  }): Promise<Source> {
    const result = await apiFetch<{ source: Source }>(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    return result.source
  },

  async delete(accountId: string, projectId: string, sourceId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}`, { method: 'DELETE' })
  },

  async analyze(accountId: string, projectId: string, sourceId: string, enabledFacetIds?: string[]): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/analyze`, {
      method: 'POST',
      body: JSON.stringify({ enabledFacetIds }),
    })
  },

  async saveFacets(
    accountId: string,
    projectId: string,
    sourceId: string,
    facets: Array<{
      facetTypeId: string
      content: string
      evidence: string
      confidence: number
      positionStart?: number
      positionEnd?: number
      suggestedEntityType?: string
      suggestedReason?: string
    }>,
    entities?: Array<{
      id: string
      type: string
      name: string
      description: string
      evidence: string
      confidence: number
    }>,
    relationships?: Array<{
      from: string
      to: string
      type: string
      evidence: string
      confidence: number
    }>,
    visualFacets?: Array<{
      facetTypeId: string
      title: string
      content: string
      evidence?: string
      confidence: number
      pageNumber?: number
      bboxX?: number
      bboxY?: number
      bboxWidth?: number
      bboxHeight?: number
      suggestedEntityType?: string
      suggestedReason?: string
    }>
  ): Promise<{
    facets: DetectedFacet[]
    facetCount: number
    textFacetCount?: number
    visualFacetCount?: number
    entitiesCreated: number
    relationshipsCreated: number
  }> {
    const result = await apiFetch<{
      facets: DetectedFacet[]
      facetCount: number
      textFacetCount?: number
      visualFacetCount?: number
      entitiesCreated: number
      relationshipsCreated: number
    }>(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets`, {
      method: 'POST',
      body: JSON.stringify({ facets, entities, relationships, visualFacets }),
    })
    return result
  },

  async updateFacet(accountId: string, projectId: string, sourceId: string, facetId: string, data: {
    action: 'accept' | 'reject'
    entityType?: string
  }): Promise<DetectedFacet> {
    const result = await apiFetch<{ facet: DetectedFacet }>(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/${facetId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    return result.facet
  },

  async bulkUpdateFacets(accountId: string, projectId: string, sourceId: string, facetIds: string[], action: 'accept' | 'reject'): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/bulk`, {
      method: 'POST',
      body: JSON.stringify({ facetIds, action }),
    })
  },

  async getFacetConfig(accountId: string, projectId: string): Promise<FacetConfiguration[]> {
    const result = await apiFetch<{ configurations: FacetConfiguration[] }>(`/api/accounts/${accountId}/projects/${projectId}/sources/facet-config`)
    return result.configurations
  },

  async updateFacetConfig(accountId: string, projectId: string, configs: Array<{
    facetTypeId: string
    isEnabled: boolean
    customEntityType?: string
  }>): Promise<FacetConfiguration[]> {
    const result = await apiFetch<{ configurations: FacetConfiguration[] }>(`/api/accounts/${accountId}/projects/${projectId}/sources/facet-config`, {
      method: 'PUT',
      body: JSON.stringify({ configurations: configs }),
    })
    return result.configurations
  },

  // Entity creation from facets
  async createEntityFromFacet(accountId: string, projectId: string, sourceId: string, facetId: string, data?: {
    entityType?: string
    entityLabel?: string
    entityDescription?: string
  }): Promise<{
    success: boolean
    facet: DetectedFacet
    entity: { uri: string; type: string; label: string }
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/${facetId}/entity`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    })
  },

  async bulkCreateEntities(accountId: string, projectId: string, sourceId: string, facetIds: string[]): Promise<{
    success: boolean
    created: number
    failed: number
    results: Array<{
      facetId: string
      success: boolean
      entityUri?: string
      entityType?: string
      error?: string
    }>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/entities`, {
      method: 'POST',
      body: JSON.stringify({ facetIds }),
    })
  },

  // ============================================================================
  // FACET ENTITY GROUPS (Many-to-One Mapping)
  // ============================================================================

  async listGroups(accountId: string, projectId: string, sourceId: string): Promise<FacetEntityGroupWithMembers[]> {
    const result = await apiFetch<{ groups: FacetEntityGroupWithMembers[] }>(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups`
    )
    return result.groups
  },

  async createGroup(accountId: string, projectId: string, sourceId: string, data: {
    entityLabel: string
    entityType: string
    entityDescription?: string
    facetIds: string[]
    primaryFacetId?: string
  }): Promise<FacetEntityGroupWithMembers> {
    const result = await apiFetch<{ group: FacetEntityGroupWithMembers }>(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.group
  },

  async updateGroup(accountId: string, projectId: string, sourceId: string, groupId: string, data: {
    entityLabel?: string
    entityType?: string
    entityDescription?: string
    status?: GroupStatus
    addFacetIds?: string[]
    removeFacetIds?: string[]
    primaryFacetId?: string
  }): Promise<FacetEntityGroupWithMembers> {
    const result = await apiFetch<{ group: FacetEntityGroupWithMembers }>(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups/${groupId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
    return result.group
  },

  async deleteGroup(accountId: string, projectId: string, sourceId: string, groupId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups/${groupId}`,
      { method: 'DELETE' }
    )
  },

  async createEntityFromGroup(accountId: string, projectId: string, sourceId: string, groupId: string): Promise<{
    success: boolean
    group: FacetEntityGroupWithMembers
    entity: { uri: string; type: string; label: string }
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups/${groupId}/entity`,
      { method: 'POST' }
    )
  },

  async bulkCreateFromGroupsAndFacets(accountId: string, projectId: string, sourceId: string, data: {
    groupIds: string[]
    facetIds: string[]
  }): Promise<{
    success: boolean
    groupResults: Array<{
      groupId: string
      success: boolean
      entityUri?: string
      error?: string
    }>
    facetResults: Array<{
      facetId: string
      success: boolean
      entityUri?: string
      error?: string
    }>
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/bulk-create`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  // ============================================================================
  // FACET-ENTITY GRAPH LINKING (Many-to-One)
  // ============================================================================

  /** Link a facet to an existing entity in the graph */
  async linkFacetToEntity(accountId: string, projectId: string, sourceId: string, facetId: string, entityUri: string): Promise<{
    success: boolean
    facet: DetectedFacet
    link: { facetUri: string; entityUri: string }
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/${facetId}/link`,
      {
        method: 'POST',
        body: JSON.stringify({ entityUri }),
      }
    )
  },

  /** Bulk link multiple facets to one entity */
  async bulkLinkFacets(accountId: string, projectId: string, sourceId: string, facetIds: string[], entityUri: string): Promise<{
    success: boolean
    linked: number
    failed: number
    entityUri: string
    results: Array<{
      facetId: string
      success: boolean
      facetUri?: string
      error?: string
    }>
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/bulk-link`,
      {
        method: 'POST',
        body: JSON.stringify({ facetIds, entityUri }),
      }
    )
  },

  /** Unlink a facet from its entity */
  async unlinkFacet(accountId: string, projectId: string, sourceId: string, facetId: string): Promise<{
    success: boolean
    facet: DetectedFacet
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/${facetId}/link`,
      { method: 'DELETE' }
    )
  },

  /** Search entities with facet counts */
  async searchEntitiesForLinking(accountId: string, projectId: string, query: string, entityType?: string, limit = 20): Promise<{
    entities: Array<{
      uri: string
      label: string
      type: string
      facetCount: number
    }>
  }> {
    const params = new URLSearchParams({ q: query, limit: String(limit) })
    if (entityType) params.set('entityType', entityType)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/graph/entities/search?${params}`
    )
  },

  /** Get all facets linked to an entity */
  async getFacetsForEntity(accountId: string, projectId: string, entityUri: string): Promise<{
    facets: Array<{
      uri: string
      facetType: string
      content: string
      evidence: string
      confidence: number
      sourceUri?: string
      sourceTitle?: string
      status?: string
      createdAt?: string
    }>
    entityUri: string
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/graph/entities/${encodeURIComponent(entityUri)}/facets`
    )
  },

  /** Backfill: Sync existing mapped facets to graph */
  async backfillFacetsToGraph(accountId: string, projectId: string): Promise<{
    success: boolean
    message: string
    synced: number
    failed?: number
    errors?: string[]
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/backfill/facets-to-graph`,
      { method: 'POST' }
    )
  },

  /** Backfill: Auto-accept pending facets */
  async backfillAutoAccept(accountId: string, projectId: string): Promise<{
    success: boolean
    message: string
    updated: number
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/backfill/auto-accept`,
      { method: 'POST' }
    )
  },

  // ============================================================================
  // VISUAL ANALYSIS
  // ============================================================================

  /** Get extracted images for a source */
  async getImages(accountId: string, projectId: string, sourceId: string): Promise<{
    images: Array<{
      id: string
      sourceId: string
      mimeType: string
      pageNumber: number
      bboxX?: number | null
      bboxY?: number | null
      bboxWidth?: number | null
      bboxHeight?: number | null
      imageType: ImageType
      classificationConfidence?: number | null
      analyzed: boolean
      createdAt: string
    }>
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/images`
    )
  },

  /** Get single image with data */
  async getImage(accountId: string, projectId: string, sourceId: string, imageId: string): Promise<{
    image: SourceImage
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/images/${imageId}`
    )
  },

  /** Save extracted images */
  async saveImages(accountId: string, projectId: string, sourceId: string, images: Array<{
    id: string
    data: string
    mimeType?: string
    pageNumber: number
    width?: number
    height?: number
    bboxX?: number
    bboxY?: number
    bboxWidth?: number
    bboxHeight?: number
  }>): Promise<{
    success: boolean
    images: Array<{ id: string; originalId: string; pageNumber: number }>
    count: number
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/images`,
      {
        method: 'POST',
        body: JSON.stringify({ images }),
      }
    )
  },

  /** Get visual facets for a source */
  async getVisualFacets(accountId: string, projectId: string, sourceId: string): Promise<{
    facets: VisualFacet[]
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/visual-facets`
    )
  },

  /** Save visual facets */
  async saveVisualFacets(
    accountId: string,
    projectId: string,
    sourceId: string,
    facets: Array<{
      imageId: string
      facetType: string
      title: string
      content?: string
      evidence?: string
      confidence: number
      bboxX?: number
      bboxY?: number
      bboxWidth?: number
      bboxHeight?: number
      suggestedEntityType?: string
      suggestedReason?: string
    }>,
    classifications?: Array<{
      imageId: string
      category: string
      confidence: number
    }>
  ): Promise<{
    success: boolean
    facets: VisualFacet[]
    count: number
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/visual-facets`,
      {
        method: 'POST',
        body: JSON.stringify({ facets, classifications }),
      }
    )
  },

  /** Get PDF file data for a source */
  async getFileData(accountId: string, projectId: string, sourceId: string): Promise<{
    fileData: string  // Base64 encoded
    fileType: string | null
    fileName: string | null
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/file`
    )
  },

  // ============================================================================
  // DOCUMENT ANALYSIS WORKFLOW
  // ============================================================================

  async getAnalysisStatus(accountId: string, projectId: string, sourceId: string): Promise<{
    status: SourceStatus
    stage: string
    progress: number
    message: string
    error?: string
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/analysis-status`
    )
  },

  async getEntityGroups(accountId: string, projectId: string, sourceId: string): Promise<EntityGroupWithFacets[]> {
    const result = await apiFetch<{ entityGroups: EntityGroupWithFacets[] }>(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/entity-groups`
    )
    return result.entityGroups
  },

  async approveEntityGroup(accountId: string, projectId: string, sourceId: string, groupId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/entity-groups/${groupId}/approve`,
      { method: 'POST' }
    )
  },

  async rejectEntityGroup(accountId: string, projectId: string, sourceId: string, groupId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/entity-groups/${groupId}/reject`,
      { method: 'POST' }
    )
  },
}

// ============================================================================
// WEB SOURCES API - Website scraping and entity extraction
// ============================================================================

export interface CrawlConfig {
  maxDepth: number
  maxPages: number
  includePaths?: string[]
  excludePaths?: string[]
  delayMs?: number
}

export interface CrawlProgress {
  pagesFound: number
  pagesCrawled: number
  currentUrl?: string
  errors: Array<{ url: string; error: string }>
}

export interface WebSource {
  id: string
  sourceId: string | null
  projectId: string
  accountId: string
  url: string
  domain: string | null
  title: string | null
  description: string | null
  crawlConfig: CrawlConfig | null
  crawlStatus: 'pending' | 'fetching' | 'extracting' | 'completed' | 'error'
  crawlProgress: CrawlProgress | null
  errorMessage: string | null
  scrapedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface WebSourceStatus {
  id: string
  crawlStatus: 'pending' | 'fetching' | 'extracting' | 'completed' | 'error'
  crawlProgress: CrawlProgress | null
  errorMessage: string | null
  title: string | null
  sourceId: string | null
}

export interface ScrapeResult {
  success: boolean
  webSourceId: string
  sourceId?: string
  title?: string
  pageCount: number
  entityCount?: number
  error?: string
}

export const webSourcesApi = {
  async scrape(
    accountId: string,
    projectId: string,
    data: { url: string; crawlConfig?: CrawlConfig }
  ): Promise<ScrapeResult> {
    const result = await apiFetch<{ data: ScrapeResult }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/scrape`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.data
  },

  async list(accountId: string, projectId: string): Promise<WebSource[]> {
    const result = await apiFetch<{ data: WebSource[] }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources`
    )
    return result.data
  },

  async get(accountId: string, projectId: string, webSourceId: string): Promise<WebSource> {
    const result = await apiFetch<{ data: WebSource }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/${webSourceId}`
    )
    return result.data
  },

  async getStatus(accountId: string, projectId: string, webSourceId: string): Promise<WebSourceStatus> {
    const result = await apiFetch<{ data: WebSourceStatus }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/${webSourceId}/status`
    )
    return result.data
  },

  async getPages(
    accountId: string,
    projectId: string,
    webSourceId: string
  ): Promise<Array<{
    id: string
    url: string
    title: string | null
    depth: number
    status: string
    errorMessage: string | null
    fetchedAt: string | null
  }>> {
    const result = await apiFetch<{ data: Array<{
      id: string
      url: string
      title: string | null
      depth: number
      status: string
      errorMessage: string | null
      fetchedAt: string | null
    }> }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/${webSourceId}/pages`
    )
    return result.data
  },

  async delete(accountId: string, projectId: string, webSourceId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/${webSourceId}`,
      { method: 'DELETE' }
    )
  },
}

// ============================================================================
// FACET CATEGORY CONFIG API (Account-level Configuration)
// ============================================================================

export interface FacetCategoryConfigItem {
  id: string
  categoryId: string
  label: string
  description?: string | null
  color: string
  icon: string
  sortOrder: number
}

export interface FacetTypeMappingItem {
  id: string
  facetTypeId: string
  categoryId: string
  label: string
  description?: string | null
  color?: string | null
  icon?: string | null
  createsEntity: boolean
  defaultEntityType?: string | null
  allowedEntityTypes?: string[] | null
  isCustom: boolean
  sortOrder: number
}

// API returns categories/mappings as objects keyed by ID
export interface FacetConfigResponse {
  categories: Record<string, FacetCategoryConfigItem>
  mappings: Record<string, FacetTypeMappingItem>
  isCustomized: boolean
}

export const facetConfigApi = {
  /**
   * Get facet configuration for a project
   * Note: Facet config is project-scoped, not account-scoped
   * Returns default configuration structure for compatibility with useFacetConfig hook
   */
  async get(_accountId: string, _projectId: string): Promise<FacetConfigResponse> {
    // Return empty config with defaults - the hook will merge with static config
    return {
      categories: {},
      mappings: {},
      isCustomized: false,
    }
  },

  /** Get default configuration (for comparison/reset) */
  async getDefaults(_accountId: string, _projectId: string): Promise<FacetConfigResponse> {
    // Return defaults - UI will merge with static config
    return {
      categories: {},
      mappings: {},
      isCustomized: false,
    }
  },

  /** Initialize project with default configuration */
  async initialize(_accountId: string, _projectId: string): Promise<FacetConfigResponse> {
    // Return empty config - UI uses static defaults from interviewer.ts
    return {
      categories: {},
      mappings: {},
      isCustomized: false,
    }
  },

  /** Update category configuration (no-op - not implemented in database) */
  async updateCategories(_accountId: string, _projectId: string, _categories: Array<{
    categoryId: string
    label: string
    description?: string | null
    color: string
    icon: string
    sortOrder: number
  }>): Promise<FacetCategoryConfigItem[]> {
    // Categories are static in interviewer.ts - no database persistence
    return []
  },

  /** Update facet type mappings (no-op - not implemented in database) */
  async updateMappings(_accountId: string, _projectId: string, _mappings: Array<{
    facetTypeId: string
    categoryId: string
    label: string
    description?: string | null
    color?: string | null
    icon?: string | null
    createsEntity: boolean
    defaultEntityType?: string | null
    allowedEntityTypes?: string[] | null
    isCustom?: boolean
    sortOrder: number
  }>): Promise<FacetTypeMappingItem[]> {
    // Mappings are static in interviewer.ts - no database persistence
    // The database only stores enabled/customName/customColor per facet type
    return []
  },

  /** Reset project configuration to defaults */
  async reset(_accountId: string, _projectId: string): Promise<FacetConfigResponse> {
    // Return defaults - UI will use static config
    return {
      categories: {},
      mappings: {},
      isCustomized: false,
    }
  },
}

// ============================================================================
// SPARQL API (PROXIED THROUGH BACKEND)
// ============================================================================

export const sparqlApi = {
  async query(accountId: string, projectId: string, sparqlQuery: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/query`, {
      method: 'POST',
      body: JSON.stringify({ query: sparqlQuery }),
    })
  },

  async update(accountId: string, projectId: string, sparqlUpdate: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/update`, {
      method: 'POST',
      body: JSON.stringify({ update: sparqlUpdate }),
    })
  },

  async ask(accountId: string, projectId: string, sparqlQuery: string): Promise<boolean> {
    const result = await apiFetch<{ boolean: boolean }>(`/api/accounts/${accountId}/projects/${projectId}/sparql/query`, {
      method: 'POST',
      body: JSON.stringify({ query: sparqlQuery }),
    })
    return result.boolean
  },

  /** Create an entity in the project graph with proper typing */
  async createEntity(
    accountId: string,
    projectId: string,
    data: {
      entityType: string
      label: string
      description?: string
    }
  ): Promise<{ entityUri: string; entityType: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ============================================================================
// ENTITY TREE API - Entity validation and tree view
// ============================================================================

export interface EntityTreeInstance {
  uri: string
  label: string
  classUri: string
  className: string
  domain: 'market' | 'business' | 'solution'
  status: 'pending' | 'confirmed' | 'rejected'
  createdAt: string
  source: 'ai' | 'manual' | 'import'
  confidence?: number
}

export interface EntityTreeClassGroup {
  classUri: string
  className: string
  domain: 'market' | 'business' | 'solution'
  instances: EntityTreeInstance[]
}

export interface EntityTreeResponse {
  entities: EntityTreeInstance[]
  byDomain: {
    market: EntityTreeClassGroup[]
    business: EntityTreeClassGroup[]
    solution: EntityTreeClassGroup[]
  }
  counts: {
    total: number
    pending: number
    confirmed: number
    rejected: number
  }
}

export const entityTreeApi = {
  /** Get all entities for tree view */
  async getTree(
    accountId: string,
    projectId: string
  ): Promise<EntityTreeResponse> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities/tree`)
  },

  /** Update entity validation status */
  async updateStatus(
    accountId: string,
    projectId: string,
    entityUri: string,
    status: 'pending' | 'confirmed' | 'rejected'
  ): Promise<{ success: boolean; entityUri: string; status: string }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sparql/entities/${encodeURIComponent(entityUri)}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }
    )
  },

  /** Bulk validate entities */
  async bulkValidate(
    accountId: string,
    projectId: string,
    entityUris: string[],
    status: 'confirmed' | 'rejected'
  ): Promise<{ success: boolean; updated: number; errors: string[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities/validate`, {
      method: 'POST',
      body: JSON.stringify({ entityUris, status }),
    })
  },

  /** Get count of pending entities */
  async getPendingCount(
    accountId: string,
    projectId: string
  ): Promise<{ count: number }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities/pending-count`)
  },

  /** Create a new entity (manual/hybrid mode) */
  async createEntity(
    accountId: string,
    projectId: string,
    entityType: string,
    label: string,
    description?: string
  ): Promise<{ entityUri: string; entityType: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities`, {
      method: 'POST',
      body: JSON.stringify({ entityType, label, description }),
    })
  },

  /** Get AI suggestions for an entity */
  async getSuggestions(
    accountId: string,
    projectId: string,
    entityUri: string,
    phaseContext?: PhaseContextForSuggestions
  ): Promise<EntitySuggestionsResponse> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities/suggestions`, {
      method: 'POST',
      body: JSON.stringify({ entityUri, phaseContext }),
    })
  },
}

// Phase context for suggestions (structured mode)
export interface PhaseContextForSuggestions {
  phaseName: string
  phaseDescription?: string
  requiredEntities?: Array<{
    classUri: string
    className: string
    domain: 'market' | 'business' | 'solution'
    minCount: number
    currentCount: number
  }>
}

// Entity suggestion types
export interface EntitySuggestion {
  id: string
  type: 'related' | 'refinement' | 'gap'
  name: string
  reason: string
  confidence: number
  entityType?: string
  domain?: 'market' | 'business' | 'solution'
}

export interface EntitySuggestionsResponse {
  suggestions: EntitySuggestion[]
  entityContext: {
    uri: string
    label: string
    entityType: string
    domain: 'market' | 'business' | 'solution'
    relatedEntities?: Array<{
      uri: string
      label: string
      entityType: string
      domain: 'market' | 'business' | 'solution'
      relationship: string
    }>
  }
}

// ============================================================================
// DOCUMENTS2 API - Entity-First Extraction
// ============================================================================

export interface Document2Entity {
  id: string
  tempId: string
  entityType: string
  name: string
  description: string | null
  confidence: number
  graphUri: string | null
  status: string
  createdAt: string
  evidence: Array<{
    id: string
    quote: string
    positionStart: number | null
    positionEnd: number | null
  }>
}

export interface Document2Relationship {
  id: string
  fromEntityId: string
  toEntityId: string
  relationshipType: string
  evidence: string | null
  confidence: number
  status: string
}

export interface Document2WithEntities {
  document: Source
  entities: Document2Entity[]
  relationships: Document2Relationship[]
}

export interface Document2ListItem {
  id: string
  title: string
  contentType: ContentType
  status: string
  analyzedAt: string | null
  createdAt: string
  entityCount: number
  relationshipCount: number
}

export const documents2Api = {
  async list(accountId: string, projectId: string): Promise<Document2ListItem[]> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2`)
  },

  async get(accountId: string, projectId: string, docId: string): Promise<Document2WithEntities> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}`)
  },

  async create(accountId: string, projectId: string, data: {
    title: string
    contentType: ContentType
    rawContent: string
    markdownContent?: string
    fileName?: string
    fileType?: 'pdf' | 'md' | 'txt'
  }): Promise<Source> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async saveEntities(accountId: string, projectId: string, docId: string, data: {
    entities: Array<{
      id: string
      type: string
      name: string
      description?: string
      confidence?: number
      evidence: Array<{ quote: string; position?: number }>
    }>
    relationships?: Array<{
      from: string
      to: string
      type: string
      evidence?: string
      confidence?: number
    }>
  }): Promise<{
    message: string
    entitiesCount: number
    relationshipsCount: number
    tempIdMapping: Record<string, string>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/entities`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async createAllGraphEntities(accountId: string, projectId: string, docId: string): Promise<{
    message: string
    entitiesCreated: number
    relationshipsCreated: number
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/create-all-graph-entities`, {
      method: 'POST',
    })
  },

  async createGraphEntities(accountId: string, projectId: string, docId: string, entityIds: string[]): Promise<{
    message: string
    entitiesCreated: number
    relationshipsCreated: number
    uriMapping: Record<string, string>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/create-graph-entities`, {
      method: 'POST',
      body: JSON.stringify({ entityIds }),
    })
  },

  async deleteEntity(accountId: string, projectId: string, docId: string, entityId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/entities/${entityId}`, {
      method: 'DELETE',
    })
  },

  async updateEntity(accountId: string, projectId: string, docId: string, entityId: string, data: {
    status?: string
    name?: string
    description?: string
    entityType?: string
  }): Promise<Document2Entity> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/entities/${entityId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  /** Analyze content using Claude API (proxied through backend to bypass CORS) */
  async analyze(accountId: string, projectId: string, data: {
    apiKey: string
    content: string
    contentType: ContentType
    maxTokens?: number
  }): Promise<{
    content: Array<{ type: 'text'; text: string }>
    usage: { input_tokens: number; output_tokens: number }
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/analyze`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Analyze with caching support - checks cache before calling Claude API */
  async analyzeWithCache(accountId: string, projectId: string, data: {
    sourceId: string
    apiKey: string
    content: string
    contentType: ContentType
    maxTokens?: number
    useCache?: boolean
  }): Promise<{
    cached: boolean
    content: Array<{ type: 'text'; text: string }>
    usage?: { input_tokens: number; output_tokens: number }
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/analyze-cached`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Invalidate cache for a specific document */
  async invalidateCache(accountId: string, projectId: string, docId: string): Promise<{
    success: boolean
    message: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/cache`, {
      method: 'DELETE',
    })
  },

  /** Get cache statistics for the project */
  async getCacheStats(accountId: string, projectId: string): Promise<{
    total: number
    cached: number
    uncached: number
    cacheHitRate: number
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/cache-stats`, {
      method: 'GET',
    })
  },
}

// ============================================================================
// DOCUMENTS3 API - Experimental entity extraction with workflow integration
// ============================================================================

export interface Document3Entity {
  id: string
  type: string
  name: string
  description?: string
  confidence?: number
  evidence: Array<{ quote: string; position?: number }>
}

export interface Document3Relationship {
  from: string
  to: string
  type: string
  evidence?: string
  confidence?: number
}

export interface Document3ExtractionMetadata {
  model: string
  provider: string
  inputTokens: number
  outputTokens: number
  durationMs: number
  usedFallback: boolean
}

export interface Document3ExtractionResult {
  success: boolean
  entities: Document3Entity[]
  relationships: Document3Relationship[]
  metadata: Document3ExtractionMetadata
  error?: string
}

export const documents3Api = {
  async list(accountId: string, projectId: string): Promise<Document2ListItem[]> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3`)
  },

  async get(accountId: string, projectId: string, docId: string): Promise<Document2WithEntities> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/${docId}`)
  },

  async create(accountId: string, projectId: string, data: {
    title: string
    contentType: ContentType
    rawContent: string
    markdownContent?: string
    fileName?: string
    fileType?: 'pdf' | 'md' | 'txt'
    fileData?: string  // Base64 encoded file data
  }): Promise<Source> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Analyze content using entity extraction service */
  async analyze(accountId: string, projectId: string, data: {
    apiKey: string
    content: string
    contentType: ContentType
  }): Promise<Document3ExtractionResult> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/analyze`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Store entities directly in Fuseki graph (graph-only architecture) */
  async store(accountId: string, projectId: string, docId: string, data: {
    entities: Document3Entity[]
    relationships?: Document3Relationship[]
  }): Promise<{
    success: boolean
    entitiesCreated: number
    evidenceCreated: number
    relationshipsCreated: number
    entityUris: Record<string, string>
    skipped: Array<{ id: string; reason: string }>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/${docId}/store`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Get PDF file data for a document */
  async getFileData(accountId: string, projectId: string, docId: string): Promise<{
    fileData: string  // Base64 encoded
    fileType: 'pdf' | 'md' | 'txt' | null
    title: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/${docId}/file`)
  },

  /** Delete a single document and all its entities/evidence */
  async delete(accountId: string, projectId: string, docId: string): Promise<{ success: boolean; message: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/${docId}`, {
      method: 'DELETE',
    })
  },

  /** Delete all documents in the project (requires confirmation) */
  async deleteAll(accountId: string, projectId: string): Promise<{
    success: boolean
    deletedCount: number
    message: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3?confirm=true`, {
      method: 'DELETE',
    })
  },
}

// ============================================================================
// DOCS API (Artifact-based)
// New endpoint that stores docs as artifacts instead of sources
// ============================================================================

export const docsApi = {
  async list(accountId: string, projectId: string): Promise<Document2ListItem[]> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs`)
  },

  async get(accountId: string, projectId: string, docId: string): Promise<Document2WithEntities> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}`)
  },

  async create(accountId: string, projectId: string, data: {
    title: string
    contentType: ContentType
    rawContent: string
    markdownContent?: string
    fileName?: string
    fileType?: 'pdf' | 'md' | 'txt'
    fileData?: string
    domain?: 'Market' | 'Business' | 'Solution'
  }): Promise<Source> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async analyze(accountId: string, projectId: string, data: {
    apiKey: string
    content: string
    contentType: ContentType
    fileData?: string // Base64-encoded PDF for fallback detection
    fileName?: string
    enableVisualAnalysis?: boolean // Explicit override
    images?: Array<{  // Pre-rendered page images
      pageNumber: number
      dataUrl: string
      base64Data: string
      mediaType: string
    }>
  }): Promise<Document3ExtractionResult> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/analyze`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async storeEntities(accountId: string, projectId: string, docId: string, data: {
    entities: ExtractedEntity3[]
    relationships?: ExtractedRelationship[]
  }): Promise<{
    success: boolean
    entitiesCreated: number
    evidenceCreated: number
    relationshipsCreated: number
    entityUris: Record<string, string>
    skipped: Array<{ id: string; reason: string }>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}/store`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getFile(accountId: string, projectId: string, docId: string): Promise<{
    fileData: string
    fileType: 'pdf' | 'md' | 'txt' | null
    title: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}/file`)
  },

  async delete(accountId: string, projectId: string, docId: string): Promise<{ success: boolean; message: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}`, {
      method: 'DELETE',
    })
  },

  async deleteAll(accountId: string, projectId: string): Promise<{
    success: boolean
    deletedCount: number
    message: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs?confirm=true`, {
      method: 'DELETE',
    })
  },

  async generateContent(accountId: string, projectId: string, docId: string, data: {
    apiKey: string
    templateId: string
    documentTitle?: string
    sectionIds?: string[]
    includeEntities?: boolean
  }): Promise<{
    success: boolean
    sections: Array<{
      sectionId: string
      heading: string
      content: string
      embeddedEntities: Array<{
        label: string
        classUri: string
        className: string
        domain: 'market' | 'business' | 'solution'
        position: { start: number; end: number }
      }>
    }>
    tiptapContent: {
      type: 'doc'
      content: Array<{
        type: string
        attrs?: Record<string, unknown>
        content?: Array<{
          type: string
          text?: string
          marks?: Array<{
            type: string
            attrs?: Record<string, unknown>
          }>
        }>
      }>
    }
    plainText: string
    entityCount: number
    error?: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}/generate`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ============================================================================
// DISCOVERY ASSISTANT API
// ============================================================================

export interface DiscoverySession {
  id: string
  projectId: string
  userId: string
  title: string
  mode: 'quick' | 'deep'
  status: 'active' | 'paused' | 'completed' | 'abandoned'
  currentPhase: 'market_reality' | 'competitive_landscape' | 'value_design' | 'business_viability'
  currentQuestion: string
  ideaText?: string | null
  completedQuestions: string[]
  createdAt: string
  updatedAt: string
}

export interface DiscoveryMessage {
  id: string
  sessionId: string
  role: 'assistant' | 'user' | 'system'
  content: string
  messageType: 'question' | 'answer' | 'follow_up' | 'pre_populated' | 'quality_feedback' | 'transition'
  questionNumber?: string | null
  metadata?: string | null
  createdAt: string
}

export interface DiscoveryAnswer {
  id: string
  sessionId: string
  questionNumber: string
  answer: string
  qualityScore?: 'shallow' | 'assumed' | 'anecdotal' | 'validated' | 'bulletproof' | null
  confidencePercent?: string | null
  extractedFacets?: string | null
  suggestedEntityType?: string | null
  suggestedEntityValue?: string | null
  isValidated: boolean
  createdAt: string
  updatedAt: string
}

export interface DiscoverySource {
  id: string
  sessionId: string
  sourceType: 'document' | 'url' | 'text'
  title: string
  content?: string | null
  analyzedFacets?: string | null
  createdAt: string
}

export const discoveryApi = {
  // Session management
  async listSessions(accountId: string, projectId: string): Promise<DiscoverySession[]> {
    const result = await apiFetch<{ sessions: DiscoverySession[] }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions`
    )
    return result.sessions
  },

  async createSession(accountId: string, projectId: string, data: {
    title?: string
    mode?: 'quick' | 'deep'
    ideaText?: string
  }): Promise<DiscoverySession> {
    const result = await apiFetch<{ session: DiscoverySession }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.session
  },

  async getSession(accountId: string, projectId: string, sessionId: string): Promise<{
    session: DiscoverySession
    messages: DiscoveryMessage[]
    answers: DiscoveryAnswer[]
    sources: DiscoverySource[]
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}`
    )
  },

  async updateSession(accountId: string, projectId: string, sessionId: string, data: {
    title?: string
    status?: 'active' | 'paused' | 'completed' | 'abandoned'
    currentPhase?: 'market_reality' | 'competitive_landscape' | 'value_design' | 'business_viability'
    currentQuestion?: string
  }): Promise<DiscoverySession> {
    const result = await apiFetch<{ session: DiscoverySession }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
    return result.session
  },

  async deleteSession(accountId: string, projectId: string, sessionId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}`,
      { method: 'DELETE' }
    )
  },

  // Messages
  async addMessage(accountId: string, projectId: string, sessionId: string, data: {
    role: 'assistant' | 'user' | 'system'
    content: string
    messageType: 'question' | 'answer' | 'follow_up' | 'pre_populated' | 'quality_feedback' | 'transition'
    questionNumber?: string
    metadata?: string
  }): Promise<DiscoveryMessage> {
    const result = await apiFetch<{ message: DiscoveryMessage }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.message
  },

  // Answers
  async saveAnswer(accountId: string, projectId: string, sessionId: string, data: {
    questionNumber: string
    answer: string
    qualityScore?: 'shallow' | 'assumed' | 'anecdotal' | 'validated' | 'bulletproof'
    confidencePercent?: string
    extractedFacets?: string
    suggestedEntityType?: string
    suggestedEntityValue?: string
    isValidated?: boolean
  }): Promise<DiscoveryAnswer> {
    const result = await apiFetch<{ answer: DiscoveryAnswer }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/answers`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.answer
  },

  // Sources
  async addSource(accountId: string, projectId: string, sessionId: string, data: {
    sourceType: 'document' | 'url' | 'text'
    title: string
    content?: string
    analyzedFacets?: string
  }): Promise<DiscoverySource> {
    const result = await apiFetch<{ source: DiscoverySource }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/sources`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.source
  },

  async deleteSource(accountId: string, projectId: string, sessionId: string, sourceId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/sources/${sourceId}`,
      { method: 'DELETE' }
    )
  },

  // Advance to next question
  async advanceSession(accountId: string, projectId: string, sessionId: string): Promise<{
    session: DiscoverySession
    isComplete: boolean
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/advance`,
      { method: 'POST' }
    )
  },

  // ============================================================================
  // DISCOVERY COACH API (Workflow-driven prompts)
  // ============================================================================

  // Get accumulated context for a session
  async getContext(accountId: string, projectId: string, sessionId: string): Promise<{
    context: DiscoveryContext
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/context`
    )
  },

  // Execute a prompt with context
  async executePrompt(accountId: string, projectId: string, sessionId: string, data: {
    stepKey: string
    promptType: 'question' | 'assess' | 'followup' | 'extract'
    userInput?: string
    apiKey: string
  }): Promise<{
    result: unknown
    text: string
    usage?: { inputTokens: number; outputTokens: number }
    context?: {
      previousSummary: string
      capturedEntities: number
      knowledgeGaps: string[]
    }
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/execute-prompt`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  // Save answer with optional extraction
  async saveStepAnswer(accountId: string, projectId: string, sessionId: string, stepKey: string, data: {
    answer: string
    assessment?: { quality: string; confidence: number }
    apiKey?: string
  }): Promise<{
    answer: DiscoveryAnswer
    extractedEntities?: unknown
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/steps/${stepKey}/answer`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  // Assemble a prompt without executing (for preview/debugging)
  async assemblePrompt(accountId: string, projectId: string, sessionId: string, data: {
    stepKey: string
    promptType: 'question' | 'assess' | 'followup' | 'extract'
    userInput?: string
  }): Promise<{
    promptKey: string
    assembled: {
      systemPrompt: string
      userPrompt: string
      model: string
      temperature: number
      maxTokens: number
    }
    context: {
      previousSummary: string
      capturedEntities: number
      knowledgeGaps: string[]
    }
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/assemble-prompt`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  // ============================================================================
  // CONVERSATIONAL COACH (New natural conversation approach)
  // ============================================================================

  /**
   * Get messages for a discovery session
   */
  async getMessages(
    accountId: string,
    projectId: string,
    sessionId: string
  ): Promise<Array<{
    id: string
    role: 'user' | 'assistant' | 'system'
    content: string
    timestamp: string
    metadata?: Record<string, unknown>
  }>> {
    const result = await apiFetch<{
      messages: Array<{
        id: string
        role: 'user' | 'assistant' | 'system'
        content: string
        timestamp: string
        metadata?: Record<string, unknown>
      }>
    }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/messages`
    )
    return result.messages
  },

  /**
   * Delete all messages for a discovery session
   */
  async deleteMessages(
    accountId: string,
    projectId: string,
    sessionId: string
  ): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/messages`,
      { method: 'DELETE' }
    )
  },

  /**
   * Export messages for a discovery session (returns download URL)
   */
  getExportUrl(
    accountId: string,
    projectId: string,
    sessionId: string,
    format: 'json' | 'markdown' = 'markdown'
  ): string {
    return `${API_URL}/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/messages/export?format=${format}`
  },

  /**
   * Send a conversational chat message (streaming)
   */
  async chatStream(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: {
      message: string
      mode?: 'guided' | 'organic'
      apiKey: string
      model?: string
    },
    onEvent: (event: ConversationalChatEvent) => void
  ): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    const token = getSessionToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(
      `${API_URL}/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/chat/stream`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      }
    )

    if (!response.ok) {
      throw new Error(`Chat stream failed: ${response.statusText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''
    let currentEventType = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEventType = line.slice(6).trim()
          continue
        }
        if (line.startsWith('data:')) {
          try {
            const data = JSON.parse(line.slice(5).trim())
            // Include the event type in the event object
            onEvent({ type: currentEventType, ...data })
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
  },

  /**
   * Send a conversational chat message (non-streaming)
   */
  async chat(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: {
      message: string
      mode?: 'guided' | 'organic'
      apiKey: string
      model?: string
    }
  ): Promise<ConversationalChatResult> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/chat`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /**
   * Get topic coverage for a session
   */
  async getTopicCoverage(
    accountId: string,
    projectId: string,
    sessionId: string,
    mode?: 'guided' | 'organic'
  ): Promise<TopicCoverageResponse> {
    const queryParams = mode ? `?mode=${mode}` : ''
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/coverage${queryParams}`
    )
  },

  /**
   * Initialize topic coverage for a new session
   */
  async initializeTopicCoverage(
    accountId: string,
    projectId: string,
    sessionId: string
  ): Promise<{ success: boolean; coverage: Record<string, TopicCoverage> }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/coverage/initialize`,
      { method: 'POST' }
    )
  },
}

// Conversational Coach Types
export type ConversationMode = 'guided' | 'organic'

export interface TopicCoverage {
  topicId: string
  level: 'not_started' | 'mentioned' | 'explored' | 'validated'
  confidence: number
  keyInsights: string[]
  extractedEntities: Array<{ type: string; label: string; description?: string }>
  signalsDetected: string[]
  lastUpdated: string
}

export interface DiscoveryTopic {
  id: string
  name: string
  description: string
  category: string
  color: string
  icon: string
}

export interface TopicCoverageResponse {
  topics: Array<{
    topic: DiscoveryTopic
    coverage: TopicCoverage | null
  }>
  suggestedNext?: string
  overallProgress: number
}

export interface TagOption {
  id: string
  label: string
  description?: string
  category?: string
}

export interface TagSelection {
  type: 'tags'
  question: string
  options: TagOption[]
  multiSelect: boolean
  allowOther: boolean
  minSelect?: number
  maxSelect?: number
}

export interface ConversationalChatEvent {
  type: 'text' | 'quick_replies' | 'tag_selection' | 'topic_update' | 'entity_extracted' | 'done' | 'error'
  content?: string
  replies?: string[]
  tagSelection?: TagSelection
  topicId?: string
  level?: string
  confidence?: number
  insights?: string[]
  entity?: { type: string; label: string; description?: string }
  messageId?: string
  topicCoverage?: Record<string, TopicCoverage>
  suggestedNextTopic?: string
  message?: string
  code?: string
}

export interface ConversationalChatResult {
  messageId: string
  content: string
  quickReplies: string[]
  topicCoverage: Record<string, TopicCoverage>
  extractedEntities: Array<{ type: string; label: string; description?: string }>
  suggestedNextTopic?: string
}

// ============================================================================
// WORKFLOW API
// ============================================================================

export interface WorkflowStep {
  key: string
  name: string
  order: number
  phase: string
  primaryEntity: string | null
  facetTypes: string[]
  qualitySignals: string[]
  followUpRules: Record<string, string>
}

export interface WorkflowPhase {
  key: string
  label: string
  color: string
  questions: number[]
}

export interface DiscoveryWorkflow {
  workflow: {
    id: string
    key: string
    name: string
    description: string
  }
  steps: WorkflowStep[]
  phases: WorkflowPhase[]
}

export const workflowApi = {
  // Get the Discovery workflow with all steps
  async getDiscoveryWorkflow(): Promise<DiscoveryWorkflow> {
    return apiFetch('/api/discovery/workflow')
  },
}

// Discovery Context type
export interface DiscoveryContext {
  sessionId: string
  projectId: string
  ideaStatement: string | null
  currentStep: number
  currentPhase: string
  answers: Array<{
    stepKey: string
    questionNumber: string
    question: string
    answer: string
    quality: string | null
    confidence: number | null
  }>
  capturedEntities: Array<{
    type: string
    label: string
    description: string | null
  }>
  uploadedSources: Array<{
    id: string
    title: string
    contentType: string
    facetCount: number
  }>
  previousSummary: string
  knowledgeGaps: string[]
}

// ============================================================================
// ENTITY RULES API (Semantic Validation Rules)
// ============================================================================

export interface EntityRule {
  id: string
  accountId: string
  entityClass: string
  entityDomain: string
  name: string
  description: string | null
  category: 'validation' | 'inference' | 'classification' | 'propagation'
  antecedent: string
  consequent: string
  swrlSyntax: string | null
  isEnabled: boolean
  isSystemRule: boolean
  severity: 'error' | 'warning' | 'info'
  example: string | null
  createdAt: string
  updatedAt: string
}

export interface GroupedRules {
  entityClass: string
  entityDomain: string
  rules: EntityRule[]
}

export const entityRulesApi = {
  /** Get all rules for account (grouped by entity class) */
  async list(accountId: string): Promise<{ rules: EntityRule[]; grouped: GroupedRules[] }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules`)
  },

  /** Get rules for specific entity class */
  async getByEntity(accountId: string, entityClass: string): Promise<{ rules: EntityRule[] }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules/${encodeURIComponent(entityClass)}`)
  },

  /** Create a new custom rule */
  async create(accountId: string, data: {
    entityClass: string
    entityDomain: string
    name: string
    description?: string
    category: 'validation' | 'inference' | 'classification' | 'propagation'
    antecedent: string
    consequent: string
    swrlSyntax?: string
    severity?: 'error' | 'warning' | 'info'
    example?: string
  }): Promise<{ rule: EntityRule }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Update a rule (enable/disable or edit custom rules) */
  async update(accountId: string, ruleId: string, data: {
    isEnabled?: boolean
    name?: string
    description?: string
    antecedent?: string
    consequent?: string
    swrlSyntax?: string
    severity?: 'error' | 'warning' | 'info'
    example?: string
  }): Promise<{ rule: EntityRule }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  /** Delete a custom rule (system rules cannot be deleted) */
  async delete(accountId: string, ruleId: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules/${ruleId}`, {
      method: 'DELETE',
    })
  },

  /** Force re-seed system rules (for dev/testing) */
  async reseed(accountId: string): Promise<{ success: boolean; count: number }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules/seed`, {
      method: 'POST',
    })
  },
}

// ============================================================================
// CANVAS AI API
// ============================================================================

export interface CanvasAIMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  metadata?: Record<string, unknown>
}

export interface CanvasAISectionScore {
  score: number
  feedback?: string
  lastUpdated: string
}

export interface CanvasAIExtractedEntity {
  type: string
  name: string
  properties: Record<string, unknown>
  confidence: 'high' | 'medium' | 'low'
  sourceSpan?: string
  sectionKey: string
}

export interface CanvasAISession {
  id: string
  canvasInstanceId: string
  canvasType: string
  workflowId: string | null
  currentSection: string | null
  sectionScores: Record<string, CanvasAISectionScore>
  conversationHistory: CanvasAIMessage[]
  extractedEntities: CanvasAIExtractedEntity[]
  contextSnapshot: Record<string, unknown>
  projectId: string
  accountId: string
  createdAt: string
  updatedAt: string
}

export interface CanvasAIWorkflow {
  id: string
  key: string
  name: string
  description: string
  canvasType: string
  sectionMapping: Record<string, string>
  color: string
  icon: string
}

export interface CanvasAIValidation {
  score: number
  strengths: string[]
  gaps: string[]
  suggestions: string[]
  feedback?: string
}

export const canvasAIApi = {
  /** Get or create an AI session for a canvas instance */
  async getSession(accountId: string, projectId: string, canvasInstanceId: string, canvasType = 'bmc'): Promise<{
    session: CanvasAISession
    workflow: CanvasAIWorkflow | null
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/session?canvasType=${canvasType}`
    )
  },

  /** Update session state */
  async updateSession(accountId: string, projectId: string, canvasInstanceId: string, data: {
    currentSection?: string
    sectionScores?: Record<string, CanvasAISectionScore>
    contextSnapshot?: Record<string, unknown>
  }): Promise<{ session: CanvasAISession }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/session`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
  },

  /** Send a message and get AI response */
  async sendMessage(accountId: string, projectId: string, canvasInstanceId: string, data: {
    message: string
    sectionKey: string
    apiKey: string
    canvasContext?: Record<string, unknown>
  }): Promise<{
    message: CanvasAIMessage
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/message`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /** Get an initial question for a section */
  async getQuestion(accountId: string, projectId: string, canvasInstanceId: string, data: {
    sectionKey: string
    apiKey: string
    canvasContext?: Record<string, unknown>
  }): Promise<{
    question: string
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/question`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /** Validate section content */
  async validateSection(accountId: string, projectId: string, canvasInstanceId: string, data: {
    sectionKey: string
    content: string | string[]
    apiKey: string
  }): Promise<{
    validation: CanvasAIValidation
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/validate`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /** Extract entities from section content */
  async extractEntities(accountId: string, projectId: string, canvasInstanceId: string, data: {
    sectionKey: string
    content: string | string[]
    apiKey: string
  }): Promise<{
    entities: CanvasAIExtractedEntity[]
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/extract`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /** Get context for a canvas */
  async getContext(accountId: string, projectId: string, canvasInstanceId: string): Promise<{
    context: {
      canvasType: string
      currentSection: string | null
      sectionScores: Record<string, CanvasAISectionScore>
      extractedEntities: CanvasAIExtractedEntity[]
      contextSnapshot: Record<string, unknown>
      linkedEntities: {
        personas: unknown[]
        segments: unknown[]
        competitors: unknown[]
        products: unknown[]
        valueProps: unknown[]
      }
    }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/context`
    )
  },

  /** Get suggestions for a section */
  async getSuggestions(accountId: string, projectId: string, canvasInstanceId: string, sectionKey: string): Promise<{
    suggestions: {
      extractedEntities: CanvasAIExtractedEntity[]
      qualityScore: number | null
      feedback: string | null
      relatedEntities: unknown[]
      bestPractices: unknown[]
    }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/suggestions/${sectionKey}`
    )
  },

  /** Generate AI suggestions for a section */
  async generateSuggestions(accountId: string, projectId: string, canvasInstanceId: string, data: {
    sectionKey: string
    apiKey: string
    canvasContext?: Record<string, string[]>
    limit?: number
  }): Promise<{
    suggestions: Array<{
      id: string
      label: string
      description?: string
      entityType: string
      confidence: 'high' | 'medium' | 'low'
      reasoning?: string
    }>
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    // Pass canvasInstanceId in body to avoid URL encoding issues with slashes
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/ai/generate-suggestions`,
      {
        method: 'POST',
        body: JSON.stringify({ ...data, canvasInstanceId }),
      }
    )
  },

  /** Get workflow info for a canvas type */
  async getWorkflow(canvasType: string): Promise<{
    workflow: CanvasAIWorkflow
    steps: Array<{
      key: string
      name: string
      description: string
      icon: string
      color: string
      stepNumber: number
      linkedEntities: string[]
      domain: string
    }>
  }> {
    return apiFetch(`/api/canvas/workflows/${canvasType}`)
  },

  /** List all canvas workflows */
  async listWorkflows(): Promise<{
    workflows: CanvasAIWorkflow[]
  }> {
    return apiFetch('/api/canvas/workflows')
  },
}

// ============================================================================
// INDUSTRY TEMPLATES API
// ============================================================================

export interface IndustryInfo {
  industryKey: string
  industryName: string
  industryDescription: string | null
  industryIcon: string | null
  industryColor: string | null
  canvasTypes: string[]
}

export interface SectionPrefill {
  notes?: string
  suggestedItems?: Array<{
    label: string
    entityType: string
    description?: string
  }>
}

export interface IndustryTemplate {
  id: string
  industryKey: string
  industryName: string
  industryDescription: string | null
  industryIcon: string | null
  industryColor: string | null
  canvasTemplateId: string
  sectionPrefills: Record<string, SectionPrefill>
  exampleContent: Record<string, unknown> | null
}

export const industryTemplatesApi = {
  /** Get all available industries */
  async getIndustries(): Promise<{ industries: IndustryInfo[] }> {
    return apiFetch('/api/canvas/industry-templates')
  },

  /** Get all canvas templates for an industry */
  async getIndustryTemplates(industryKey: string): Promise<{
    industry: {
      industryKey: string
      industryName: string
      industryDescription: string | null
      industryIcon: string | null
      industryColor: string | null
    }
    templates: Array<{
      id: string
      canvasTemplateId: string
      sectionPrefills: Record<string, SectionPrefill>
      exampleContent: Record<string, unknown> | null
    }>
  }> {
    return apiFetch(`/api/canvas/industry-templates/${industryKey}`)
  },

  /** Get a specific industry template */
  async getTemplate(industryKey: string, canvasType: string): Promise<{
    template: IndustryTemplate
  }> {
    return apiFetch(`/api/canvas/industry-templates/${industryKey}/${canvasType}`)
  },
}

// ============================================================================
// VALIDATION API
// ============================================================================

export interface AutoFixAction {
  id: string
  type: 'add_property' | 'link_to_entity' | 'create_and_link_entity' | 'set_enum_value' | 'add_classification'
  label: string
  description: string
  sparqlUpdate: string | null
  requiresUserInput: boolean
  inputSchema?: {
    type: 'entity_picker' | 'property_value' | 'enum_select'
    property?: string
    targetEntityClass?: string
    enumValues?: string[]
    placeholder?: string
  }
}

export interface Violation {
  id: string
  ruleId: string
  projectId: string
  entityUri: string
  entityLabel: string | null
  violationMessage: string | null
  suggestedFix: string | null
  status: 'open' | 'acknowledged' | 'resolved'
  resolvedAt: string | null
  resolvedById: string | null
  createdAt: string
  rule?: EntityRule
  autoFixActions?: AutoFixAction[]
}

export interface ViolationSummary {
  totalViolations: number
  byType: {
    error: number
    warning: number
    info: number
  }
  byStatus: {
    open: number
    acknowledged: number
    resolved: number
  }
  topViolations: Array<{
    ruleName: string
    count: number
    severity: string
  }>
}

export const validationApi = {
  /** Validate a single entity */
  async validateEntity(
    projectId: string,
    entityUri: string,
    entityType?: string
  ): Promise<{
    entityUri: string
    entityLabel: string | null
    violations: Violation[]
    inferredProperties?: Array<{
      property: string
      value: string
      inferredBy: string
    }>
  }> {
    return apiFetch(`/api/projects/${projectId}/validation/validate-entity`, {
      method: 'POST',
      body: JSON.stringify({ entityUri, entityType }),
    })
  },

  /** Get violations for a specific entity */
  async getViolationsByEntity(
    projectId: string,
    entityUri: string
  ): Promise<{ violations: Violation[] }> {
    const encodedUri = encodeURIComponent(entityUri)
    return apiFetch(`/api/projects/${projectId}/validation/violations/${encodedUri}`)
  },

  /** Get all violations for a project */
  async getViolationsByProject(
    projectId: string,
    options?: {
      status?: 'open' | 'acknowledged' | 'resolved'
      severity?: 'error' | 'warning' | 'info'
      limit?: number
      offset?: number
    }
  ): Promise<{ violations: Violation[] }> {
    const params = new URLSearchParams()
    if (options?.status) params.set('status', options.status)
    if (options?.severity) params.set('severity', options.severity)
    if (options?.limit) params.set('limit', options.limit.toString())
    if (options?.offset) params.set('offset', options.offset.toString())

    const query = params.toString() ? `?${params.toString()}` : ''
    return apiFetch(`/api/projects/${projectId}/validation/violations${query}`)
  },

  /** Get violation summary for a project */
  async getViolationSummary(projectId: string): Promise<ViolationSummary> {
    return apiFetch(`/api/projects/${projectId}/validation/summary`)
  },

  /** Get violation counts for multiple entities */
  async getViolationCounts(
    projectId: string,
    entityUris: string[]
  ): Promise<{
    counts: Record<string, { total: number; byType: { error: number; warning: number; info: number } }>
  }> {
    return apiFetch(`/api/projects/${projectId}/validation/violation-counts`, {
      method: 'POST',
      body: JSON.stringify({ entityUris }),
    })
  },

  /** Acknowledge a violation */
  async acknowledgeViolation(
    projectId: string,
    violationId: string,
    userId?: string
  ): Promise<{ violation: Violation }> {
    return apiFetch(`/api/projects/${projectId}/validation/acknowledge/${violationId}`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    })
  },

  /** Reopen a violation */
  async reopenViolation(
    projectId: string,
    violationId: string
  ): Promise<{ violation: Violation }> {
    return apiFetch(`/api/projects/${projectId}/validation/reopen/${violationId}`, {
      method: 'POST',
    })
  },

  /** Apply an auto-fix action */
  async applyAutoFix(
    projectId: string,
    violationId: string,
    actionId: string,
    userInput?: {
      propertyValue?: string
      selectedEntityUri?: string
      selectedEnumValue?: string
    }
  ): Promise<{
    success: boolean
    message: string
    violations: Violation[]
  }> {
    return apiFetch(`/api/projects/${projectId}/validation/auto-fix/${violationId}`, {
      method: 'POST',
      body: JSON.stringify({ actionId, userInput }),
    })
  },

  /** Validate all entities in a project */
  async validateProject(
    projectId: string,
    entityTypes?: string[]
  ): Promise<{
    totalEntities: number
    totalViolations: number
    violationsByType: { error: number; warning: number; info: number }
  }> {
    return apiFetch(`/api/projects/${projectId}/validation/validate-project`, {
      method: 'POST',
      body: JSON.stringify({ entityTypes }),
    })
  },
}

// ============================================================================
// AI ANALYSIS API
// ============================================================================

export interface AnalyzeDocumentRequest {
  sourceId: string
  content: string
  contentType: string
  pageImages?: Array<{
    id: string
    data: string
    mimeType?: string
    pageNumber?: number
    width?: number
    height?: number
  }>
  accountId?: string
  projectId?: string
}

export interface AnalyzeDocumentResponse {
  sourceId: string
  facets: Array<{
    facetTypeId: string
    title?: string
    content: string
    evidence: string
    confidence: number
    positionStart?: number
    positionEnd?: number
    suggestedEntityType?: string
    suggestedReason?: string
  }>
  visualChunks: Array<{
    pageNumber: number
    description: string
    chunkType: string
    textContext?: string
    confidence: number
  }>
  visualFacets: Array<{
    facetTypeId: string
    title: string
    content: string
    evidence: string
    confidence: number
    imageId: string
    isVisual: boolean
  }>
  analysisTime: number
  usage?: {
    inputTokens: number
    outputTokens: number
  }
}

// ============================================================================
// KNOWLEDGE BASE API (RAG)
// ============================================================================

export type KnowledgeSourceType = 'file' | 'url' | 'manual'
export type KnowledgeCategory = 'framework' | 'case_study' | 'best_practice' | 'template' | 'research' | 'guide' | 'book' | 'other'
export type KnowledgeStatus = 'pending' | 'processing' | 'ready' | 'error'

export interface ProcessingProgress {
  step: 'extracting' | 'chunking' | 'embedding' | 'storing' | 'complete' | 'error'
  progress: number // 0-100
  message: string
  totalChunks?: number
  processedChunks?: number
  startedAt?: string
  updatedAt?: string
}

export interface KnowledgeSource {
  id: string
  title: string
  description?: string | null
  sourceType: KnowledgeSourceType
  category?: KnowledgeCategory | null
  url?: string | null
  fileName?: string | null
  status: KnowledgeStatus
  chunkCount: number
  tags: string[]
  createdAt: string
  updatedAt: string
  processedAt?: string | null
  errorMessage?: string | null
  processingProgress?: ProcessingProgress | null
}

export interface KnowledgeQueryResult {
  chunkId: string
  content: string
  similarity: number
  sourceId: string
  sourceTitle: string
  sourceUrl?: string | null
  sourceCategory?: string | null
  topic?: string | null
}

export interface KnowledgeQueryResponse {
  query: string
  results: KnowledgeQueryResult[]
  totalFound: number
  model: string
}

export interface KnowledgeStats {
  totalSources: number
  totalChunks: number
  sourcesByStatus: Record<KnowledgeStatus, number>
  sourcesByCategory: Record<string, number>
  recentQueries: number
}

// PM Topics for Training Guide
export interface PMTopic {
  id: string
  name: string
  description: string
  icon: string
  color: 'market' | 'business' | 'solution'
  keywords: string[]
  chunkCount: number
}

export interface TopicContentResponse {
  topic: Omit<PMTopic, 'chunkCount'> | undefined
  sources: Array<{
    sourceId: string
    sourceTitle: string
    sourceUrl: string | null
    sourceCategory: string | null
    chunks: Array<{
      id: string
      content: string
      chunkIndex: number
      subtopic: string | null
    }>
  }>
  totalChunks: number
}

// AI-synthesized training guide
export interface GuideSection {
  title: string
  content: string
  keyPoints: string[]
}

export interface TopicGuide {
  id: string
  topicId: string
  title: string
  overview: string
  sections: GuideSection[]
  generatedAt: string
  sourceChunkCount: number
  version: number
  isStale: boolean
}

export const knowledgeApi = {
  /** List all knowledge sources */
  async listSources(accountId: string, options?: {
    category?: KnowledgeCategory
    status?: KnowledgeStatus
  }): Promise<{ sources: KnowledgeSource[]; total: number }> {
    const params = new URLSearchParams()
    if (options?.category) params.set('category', options.category)
    if (options?.status) params.set('status', options.status)
    const query = params.toString() ? `?${params.toString()}` : ''
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources${query}`)
  },

  /** Add a URL source */
  async addUrl(accountId: string, data: {
    url: string
    title?: string
    category?: KnowledgeCategory
    tags?: string[]
    description?: string
  }): Promise<{ source: KnowledgeSource }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources/url`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Add manual content */
  async addManual(accountId: string, data: {
    title: string
    content: string
    category?: KnowledgeCategory
    tags?: string[]
    description?: string
  }): Promise<{ source: KnowledgeSource }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources/manual`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Upload a file (PDF, TXT, MD, DOCX) */
  async uploadFile(accountId: string, data: {
    file: File
    title?: string
    category?: KnowledgeCategory
    tags?: string[]
    description?: string
  }): Promise<{ source: KnowledgeSource }> {
    const formData = new FormData()
    formData.append('file', data.file)
    if (data.title) formData.append('title', data.title)
    if (data.category) formData.append('category', data.category)
    if (data.description) formData.append('description', data.description)
    if (data.tags?.length) formData.append('tags', data.tags.join(','))

    const token = getSessionToken()
    const headers: Record<string, string> = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    // Note: Don't set Content-Type for FormData - browser sets it with boundary

    const response = await fetch(`${API_URL}/api/accounts/${accountId}/knowledge/sources/file`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    })

    const result = await response.json()
    if (!response.ok) {
      throw new Error(result.error || result.message || `Upload failed: ${response.status}`)
    }
    return result
  },

  /** Process a source (generate embeddings) */
  async processSource(accountId: string, sourceId: string, data?: {
    // Unified embedding config
    provider?: 'openai' | 'mistral' | 'ollama' | 'voyage'
    apiKey?: string
    baseUrl?: string
    // Legacy support (deprecated)
    openaiApiKey?: string
    voyageApiKey?: string
  }): Promise<{ message: string; sourceId: string; status: string }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources/${sourceId}/process`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    })
  },

  /** Delete a source */
  async deleteSource(accountId: string, sourceId: string): Promise<{ message: string }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources/${sourceId}`, {
      method: 'DELETE',
    })
  },

  /** Query the knowledge base */
  async query(accountId: string, data: {
    q: string
    topK?: number
    minSimilarity?: number
    category?: KnowledgeCategory
  }, headers?: {
    openaiApiKey?: string
    voyageApiKey?: string
  }): Promise<KnowledgeQueryResponse> {
    const params = new URLSearchParams({ q: data.q })
    if (data.topK) params.set('topK', data.topK.toString())
    if (data.minSimilarity) params.set('minSimilarity', data.minSimilarity.toString())
    if (data.category) params.set('category', data.category)

    const requestHeaders: Record<string, string> = {}
    if (headers?.openaiApiKey) requestHeaders['X-OpenAI-API-Key'] = headers.openaiApiKey
    if (headers?.voyageApiKey) requestHeaders['X-Voyage-API-Key'] = headers.voyageApiKey

    return apiFetch(`/api/accounts/${accountId}/knowledge/query?${params.toString()}`, {
      headers: requestHeaders,
    })
  },

  /** Submit feedback on a query result */
  async submitFeedback(accountId: string, data: {
    chunkId: string
    query: string
    helpful: boolean
    comment?: string
  }): Promise<{ feedback: { id: string } }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/feedback`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Get knowledge base stats */
  async getStats(accountId: string): Promise<KnowledgeStats> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/stats`)
  },

  /** Get all PM topics with content counts */
  async getTopics(accountId: string): Promise<{ topics: PMTopic[] }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics`)
  },

  /** Get content for a specific topic */
  async getTopicContent(
    accountId: string,
    topicId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<TopicContentResponse> {
    const params = new URLSearchParams()
    if (options?.limit) params.set('limit', options.limit.toString())
    if (options?.offset) params.set('offset', options.offset.toString())
    const query = params.toString() ? `?${params.toString()}` : ''
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/${topicId}/content${query}`)
  },

  /** Backfill topic classification for unclassified chunks */
  async backfillTopics(
    accountId: string,
    batchSize?: number
  ): Promise<{
    message: string
    classified: number
    unmatched: number
    processed: number
    hasMore: boolean
  }> {
    const query = batchSize ? `?batchSize=${batchSize}` : ''
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/backfill${query}`, {
      method: 'POST',
    })
  },

  /** Get synthesized guide for a topic */
  async getTopicGuide(
    accountId: string,
    topicId: string
  ): Promise<{ guide: TopicGuide | null; exists: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/${topicId}/guide`)
  },

  /** Generate or regenerate a guide for a topic */
  async generateTopicGuide(
    accountId: string,
    topicId: string,
    claudeApiKey: string
  ): Promise<{ guide: TopicGuide; message: string }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/${topicId}/guide/generate`, {
      method: 'POST',
      headers: {
        'X-Claude-API-Key': claudeApiKey,
      },
    })
  },

  /** Delete a topic guide */
  async deleteTopicGuide(accountId: string, topicId: string): Promise<{ message: string }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/${topicId}/guide`, {
      method: 'DELETE',
    })
  },
}

// ============================================================================
// CANVAS COACH API (Conversational Canvas Coaching)
// ============================================================================

export type CanvasType =
  | 'bmc'
  | 'vpc'
  | 'positioning'
  | 'psc'
  | 'persona'

export type CanvasCoverageLevel = 'not_started' | 'mentioned' | 'explored' | 'validated'

export interface CanvasSectionDefinition {
  id: string
  name: string
  description: string
  canvasType: CanvasType
  color: string
  icon: string
  order: number
}

export interface CanvasSectionCoverage {
  sectionId: string
  level: CanvasCoverageLevel
  confidence: number
  qualityScore?: number
  keyInsights: string[]
  extractedEntities: Array<{
    type: string
    label: string
    description?: string
    sectionId?: string
    confidence: number
  }>
  signalsDetected: string[]
  lastUpdated: Date
}

export interface CanvasCoachSession {
  id: string
  canvasInstanceId: string
  canvasType: CanvasType
  projectId: string
  accountId: string
  createdAt: string
  updatedAt: string
}

export interface CanvasCoverageResponse {
  sections: Array<{
    section: CanvasSectionDefinition
    coverage: CanvasSectionCoverage | null
  }>
  suggestedNext?: string
  overallProgress: number
  canvasType: CanvasType
}

export type QuickActionType = 'add_examples' | 'skip' | 'show_more' | 'navigate_section' | 'text'

export interface QuickAction {
  label: string
  type: QuickActionType
  payload?: string
}

export interface CanvasCoachChatEvent {
  type: 'text' | 'quick_actions' | 'section_update' | 'entity_extracted' | 'suggestion' | 'done' | 'error'
  content?: string
  actions?: QuickAction[]
  sectionId?: string
  level?: string
  confidence?: number
  insights?: string[]
  // For entity_extracted events
  entityType?: string
  label?: string
  description?: string
  message?: string
  messageId?: string
  sectionCoverage?: Record<string, CanvasSectionCoverage>
  suggestedNextSection?: string
  code?: string
}

export interface CanvasCoachChatResult {
  messageId: string
  content: string
  quickActions: QuickAction[]
  sectionCoverage: Record<string, CanvasSectionCoverage>
  extractedEntities: Array<{ type: string; label: string; description?: string; sectionId?: string }>
  suggestedNextSection?: string
}

export interface CanvasDirectEditResponse {
  sectionCoverage: CanvasSectionCoverage
  suggestedNextSection?: string
  suggestionMessage?: string
}

export const canvasCoachApi = {
  /**
   * Create or get a coaching session for a canvas instance
   */
  async getOrCreateSession(
    accountId: string,
    projectId: string,
    instanceId: string,
    canvasType: CanvasType
  ): Promise<{ session: CanvasCoachSession }> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    try {
      // Try to get existing session
      const existing = await apiFetch<{ session: CanvasCoachSession | null }>(
        `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/sessions`
      )
      if (existing.session) {
        return { session: existing.session }
      }
    } catch {
      // Session doesn't exist, create one
    }

    // Create new session
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/sessions`,
      {
        method: 'POST',
        body: JSON.stringify({ canvasType }),
      }
    )
  },

  /**
   * Send a chat message with streaming response
   */
  async chatStream(
    accountId: string,
    projectId: string,
    instanceId: string,
    data: {
      message: string
      mode?: 'guided' | 'organic'
      apiKey: string
      model?: string
      currentSection?: string
      canvasContent?: string
    },
    onEvent: (event: CanvasCoachChatEvent) => void
  ): Promise<void> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    const token = getSessionToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(
      `${API_URL}/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/chat/stream`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      }
    )

    if (!response.ok) {
      throw new Error(`Canvas coach chat stream failed: ${response.statusText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''
    let currentEventType = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEventType = line.slice(6).trim()
          continue
        }
        if (line.startsWith('data:')) {
          try {
            const data = JSON.parse(line.slice(5).trim())
            onEvent({ type: currentEventType as CanvasCoachChatEvent['type'], ...data })
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
  },

  /**
   * Send a chat message (non-streaming)
   */
  async chat(
    accountId: string,
    projectId: string,
    instanceId: string,
    data: {
      message: string
      mode?: 'guided' | 'organic'
      apiKey: string
      model?: string
      currentSection?: string
      canvasContent?: string
    }
  ): Promise<CanvasCoachChatResult> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/chat`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /**
   * Get section coverage for a canvas instance
   */
  async getCoverage(
    accountId: string,
    projectId: string,
    instanceId: string,
    mode: 'guided' | 'organic' = 'organic'
  ): Promise<CanvasCoverageResponse> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/coverage?mode=${mode}`
    )
  },

  /**
   * Update coverage from direct canvas edits (hybrid mode)
   */
  async updateFromDirectEdit(
    accountId: string,
    projectId: string,
    instanceId: string,
    data: {
      sectionId: string
      items: Array<{ id: string; label: string; description?: string }>
      action: 'add' | 'update' | 'remove'
      mode?: 'guided' | 'organic'
    }
  ): Promise<CanvasDirectEditResponse> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/coverage/update`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },
}

// ============================================================================
// AI ANALYSIS API
// ============================================================================

export const aiAnalysisApi = {
  /**
   * Analyze a document using the backend workflow system
   * This routes through the document-analysis workflow for prompt management and logging
   */
  async analyzeDocument(
    request: AnalyzeDocumentRequest,
    claudeApiKey?: string
  ): Promise<AnalyzeDocumentResponse> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Pass Claude API key to backend if provided
    if (claudeApiKey) {
      headers['x-claude-api-key'] = claudeApiKey
    }

    return apiFetch<AnalyzeDocumentResponse>('/api/ai/analyze', {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    })
  },

  /**
   * Analyze a single image using visual analysis
   */
  async analyzeImage(
    sourceId: string,
    image: {
      id: string
      data: string
      mimeType?: string
      pageNumber?: number
    },
    existingFacets?: Array<Record<string, unknown>>,
    claudeApiKey?: string
  ): Promise<{
    sourceId: string
    classification?: {
      category: string
      confidence: number
      should_analyze: boolean
    }
    extraction?: Record<string, unknown>
    facets?: Array<Record<string, unknown>>
    analysisTime: number
  }> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (claudeApiKey) {
      headers['x-claude-api-key'] = claudeApiKey
    }

    return apiFetch('/api/ai/analyze-image', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        sourceId,
        image,
        existingFacets,
      }),
    })
  },

  /**
   * Get workflow configuration
   */
  async getWorkflow(key: string): Promise<{
    workflow: {
      id: string
      key: string
      name: string
      description: string
      steps: Array<{
        id: string
        key: string
        name: string
        description: string
        prompts: Array<{
          promptId: string
          promptKey: string
          promptName: string
        }>
      }>
    }
  }> {
    return apiFetch(`/api/ai/workflows/${key}`)
  },
}

// ============================================================================
// SETTINGS API
// ============================================================================

export interface FusekiConfigResponse {
  url: string
  dataset: string
  username: string
  hasPassword: boolean
}

export interface FusekiConfigUpdate {
  url?: string
  dataset?: string
  username?: string
  password?: string
}

export const settingsApi = {
  /**
   * Get current Fuseki configuration
   */
  async getFusekiConfig(): Promise<{ config: FusekiConfigResponse }> {
    return apiFetch('/api/settings/fuseki')
  },

  /**
   * Update Fuseki configuration
   */
  async updateFusekiConfig(config: FusekiConfigUpdate): Promise<{ config: FusekiConfigResponse }> {
    return apiFetch('/api/settings/fuseki', {
      method: 'PUT',
      body: JSON.stringify(config),
    })
  },

  /**
   * Test Fuseki connection with current or provided config
   */
  async testFusekiConnection(
    config?: FusekiConfigUpdate
  ): Promise<{ success: boolean; message: string }> {
    return apiFetch('/api/settings/fuseki/test', {
      method: 'POST',
      body: JSON.stringify(config || {}),
    })
  },

  /**
   * Clear graph data (Fuseki) only
   * @param projectId - Optional project ID to clear only that project's graph
   */
  async clearGraphData(projectId?: string): Promise<{ success: boolean; message: string }> {
    const url = projectId ? `/api/settings/data/clear-graph?projectId=${projectId}` : `/api/settings/data/clear-graph`
    return apiFetch(url, {
      method: 'POST',
    })
  },

  /**
   * Clear relational data (PostgreSQL) only
   * @param projectId - Optional project ID to clear only that project's data
   */
  async clearRelationalData(projectId?: string): Promise<{ success: boolean; message: string }> {
    const url = projectId ? `/api/settings/data/clear-relational?projectId=${projectId}` : `/api/settings/data/clear-relational`
    return apiFetch(url, {
      method: 'POST',
    })
  },

  /**
   * Clear all data (both graph and relational)
   * @param projectId - Optional project ID to clear only that project's data
   */
  async clearAllData(projectId?: string): Promise<{ success: boolean; message: string }> {
    const url = projectId ? `/api/settings/data/clear-all?projectId=${projectId}` : `/api/settings/data/clear-all`
    return apiFetch(url, {
      method: 'POST',
    })
  },
}

// ============================================================================
// AI OPERATION CONFIGURATION API
// ============================================================================

export interface AIOperationConfig {
  operation: string
  name: string
  description: string
  category: string
  provider: string
  model: string
  temperature: number
  maxTokens: number
  requiresVision: boolean
  recommendedQuality: string
  isOverridden: boolean
  overrideId?: string
  updatedBy?: string
  updatedAt?: Date
}

export interface AIOperationConfigUpdate {
  provider?: 'claude' | 'gemini' | 'ollama'
  model?: string
  temperature?: number
  maxTokens?: number
}

export interface AIOperationBulkUpdate {
  operation: string
  provider?: 'claude' | 'gemini' | 'ollama'
  model?: string
  temperature?: number
  maxTokens?: number
}

export const aiConfigApi = {
  /**
   * Get all AI operation configs for an account (with overrides applied)
   */
  async getOperations(accountId: string): Promise<{ operations: AIOperationConfig[] }> {
    return apiFetch(`/api/accounts/${accountId}/ai/operations`)
  },

  /**
   * Get single operation config (with override applied)
   */
  async getOperation(
    accountId: string,
    operation: string
  ): Promise<{ operation: string; config: AIOperationConfigUpdate }> {
    return apiFetch(`/api/accounts/${accountId}/ai/operations/${operation}`)
  },

  /**
   * Update account override for an operation
   */
  async updateOperation(
    accountId: string,
    operation: string,
    config: AIOperationConfigUpdate
  ): Promise<{ override: AIOperationConfig }> {
    console.log('[aiConfigApi] 🔷 updateOperation called:', { accountId, operation, config })
    const result = await apiFetch<{ override: AIOperationConfig }>(`/api/accounts/${accountId}/ai/operations/${operation}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    })
    console.log('[aiConfigApi] 🔷 updateOperation result:', result)
    return result
  },

  /**
   * Delete account override (revert to system default)
   */
  async deleteOverride(accountId: string, operation: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/ai/operations/${operation}`, {
      method: 'DELETE',
    })
  },

  /**
   * Bulk update multiple operations at once
   */
  async bulkUpdate(
    accountId: string,
    updates: AIOperationBulkUpdate[]
  ): Promise<{
    updated: number
    overrides: AIOperationConfig[]
    errors?: Array<{ operation: string; error: string }>
  }> {
    return apiFetch(`/api/accounts/${accountId}/ai/operations/bulk-update`, {
      method: 'POST',
      body: JSON.stringify({ updates }),
    })
  },
}

// ============================================================================
// JOURNEY FRAMEWORK API
// ============================================================================

export interface JourneyFrameworkResponse {
  id: string
  accountId: string
  name: string
  description: string | null
  isDefault: boolean
  isSystem: boolean
  config: JourneyFramework
  createdAt: string
  updatedAt: string
}

export interface ProjectJourneyResponse {
  id: string
  projectId: string
  frameworkId: string | null
  goalId: string | null
  currentPhaseId: string
  phaseProgress: Record<string, number>
  completedActivities: string[]
  completedArtifacts: string[]
  createdAt: string
  updatedAt: string
}

export const journeyApi = {
  /**
   * Get the default framework for the account
   */
  async getFramework(accountId: string): Promise<{ framework: JourneyFrameworkResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/framework`)
  },

  /**
   * Get all frameworks for the account
   */
  async getFrameworks(accountId: string): Promise<{ frameworks: JourneyFrameworkResponse[] }> {
    return apiFetch(`/api/accounts/${accountId}/journey/frameworks`)
  },

  /**
   * Get project journey progress with framework
   */
  async getProjectJourney(
    accountId: string,
    projectId: string
  ): Promise<{ journey: ProjectJourneyResponse; framework: JourneyFrameworkResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey`)
  },

  /**
   * Update project journey progress
   */
  async updateProgress(
    accountId: string,
    projectId: string,
    updates: {
      currentPhaseId?: string
      phaseProgress?: Record<string, number>
      goalId?: string
    }
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/progress`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  },

  /**
   * Mark an activity as completed
   */
  async completeActivity(
    accountId: string,
    projectId: string,
    activityId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/activities/complete`, {
      method: 'POST',
      body: JSON.stringify({ activityId }),
    })
  },

  /**
   * Uncomplete an activity
   */
  async uncompleteActivity(
    accountId: string,
    projectId: string,
    activityId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/activities/uncomplete`, {
      method: 'POST',
      body: JSON.stringify({ activityId }),
    })
  },

  /**
   * Mark an artifact as completed
   */
  async completeArtifact(
    accountId: string,
    projectId: string,
    artifactId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/artifacts/complete`, {
      method: 'POST',
      body: JSON.stringify({ artifactId }),
    })
  },

  /**
   * Select a goal path
   */
  async selectGoal(
    accountId: string,
    projectId: string,
    goalId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/goal`, {
      method: 'POST',
      body: JSON.stringify({ goalId }),
    })
  },

  /**
   * Advance to next phase
   */
  async advancePhase(
    accountId: string,
    projectId: string,
    phaseId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/advance`, {
      method: 'POST',
      body: JSON.stringify({ phaseId }),
    })
  },

  /**
   * Get configurable items (artifacts and entities) for phase configuration
   */
  async getConfigurableItems(
    accountId: string
  ): Promise<ConfigurableItemsResponse> {
    return apiFetch(`/api/accounts/${accountId}/journey/configurable-items`)
  },

  /**
   * Update phase configuration (artifacts and entities)
   */
  async updatePhaseConfig(
    accountId: string,
    frameworkId: string,
    phaseId: string,
    config: UpdatePhaseConfigRequest
  ): Promise<{ framework: JourneyFrameworkResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/framework/${frameworkId}/phases/${phaseId}/config`, {
      method: 'PATCH',
      body: JSON.stringify(config),
    })
  },
}

// ============================================================================
// ARTIFACTS API
// ============================================================================

import type {
  ArtifactTypeEnum,
  ArtifactDomain,
  ArtifactStatus,
  Artifact,
  ArtifactTemplate,
  ArtifactTaxonomy,
  ArtifactVersion,
  ArtifactEntityLink,
} from '@ispot/shared'

export interface ArtifactFilters {
  type?: ArtifactTypeEnum
  subType?: string
  domain?: ArtifactDomain
  status?: ArtifactStatus
  journeyPhaseId?: string
  search?: string
}

export interface CreateArtifactRequest {
  type: ArtifactTypeEnum
  subType: string
  domain: ArtifactDomain
  title: string
  description?: string
  templateId?: string
  content?: Record<string, unknown>
  status?: 'draft' | 'active'
  journeyPhaseId?: string
  journeyActivityId?: string
}

export interface UpdateArtifactRequest {
  title?: string
  description?: string
  content?: Record<string, unknown>
  status?: ArtifactStatus
}

export const artifactsApi = {
  // Project-scoped artifact CRUD
  async list(
    accountId: string,
    projectId: string,
    filters?: ArtifactFilters
  ): Promise<{ artifacts: Artifact[]; total: number }> {
    const params = new URLSearchParams()
    if (filters?.type) params.set('type', filters.type)
    if (filters?.subType) params.set('subType', filters.subType)
    if (filters?.domain) params.set('domain', filters.domain)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.journeyPhaseId) params.set('journeyPhaseId', filters.journeyPhaseId)
    if (filters?.search) params.set('search', filters.search)
    const query = params.toString()
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts${query ? `?${query}` : ''}`)
  },

  async get(
    accountId: string,
    projectId: string,
    artifactId: string
  ): Promise<{ artifact: Artifact }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}`)
  },

  async create(
    accountId: string,
    projectId: string,
    data: CreateArtifactRequest
  ): Promise<{ artifact: Artifact }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async update(
    accountId: string,
    projectId: string,
    artifactId: string,
    data: UpdateArtifactRequest
  ): Promise<{ artifact: Artifact }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  async archive(
    accountId: string,
    projectId: string,
    artifactId: string
  ): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}`, {
      method: 'DELETE',
    })
  },

  // Version management
  async listVersions(
    accountId: string,
    projectId: string,
    artifactId: string
  ): Promise<{ versions: ArtifactVersion[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/versions`)
  },

  async getVersion(
    accountId: string,
    projectId: string,
    artifactId: string,
    version: number
  ): Promise<{ version: number; content: Record<string, unknown> }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/versions/${version}`)
  },

  async restoreVersion(
    accountId: string,
    projectId: string,
    artifactId: string,
    version: number
  ): Promise<{ artifact: Artifact; restored: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/versions/${version}/restore`, {
      method: 'POST',
    })
  },

  // Entity links
  async getEntityLinks(
    accountId: string,
    projectId: string,
    artifactId: string,
    sectionId?: string
  ): Promise<{ links: ArtifactEntityLink[] }> {
    const query = sectionId ? `?sectionId=${sectionId}` : ''
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/entities${query}`)
  },

  async linkEntity(
    accountId: string,
    projectId: string,
    artifactId: string,
    data: {
      entityUri: string
      entityType: string
      entityLabel: string
      linkType?: 'reference' | 'derived' | 'supports'
      sectionId?: string
      contextNote?: string
      sortOrder?: number
    }
  ): Promise<{ link: ArtifactEntityLink }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/entities`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async updateEntityLink(
    accountId: string,
    projectId: string,
    artifactId: string,
    linkId: string,
    data: { entityLabel?: string; contextNote?: string | null }
  ): Promise<{ link: ArtifactEntityLink }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/entities/${linkId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  async unlinkEntity(
    accountId: string,
    projectId: string,
    artifactId: string,
    linkId: string
  ): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/entities/${linkId}`, {
      method: 'DELETE',
    })
  },

  // Create entity in graph AND link to canvas (for coach suggestions)
  async createAndLinkEntity(
    accountId: string,
    projectId: string,
    artifactId: string,
    data: {
      sectionId: string
      label: string
      entityType: string
      description?: string
      note?: string
      sortOrder?: number
      source?: 'user' | 'ai' | 'document' | 'import'
      confidence?: number
    }
  ): Promise<{ entityUri: string; entityType: string; link: ArtifactEntityLink }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/entities/create`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Migration
  async migrateCanvas(
    accountId: string,
    projectId: string,
    data: {
      templateKey: string
      title: string
      sections: Record<string, {
        notes?: string
        items?: Array<{
          id: string
          entityUri: string
          label: string
          entityType?: string
          note?: string
          sortOrder: number
          addedAt: string
        }>
      }>
    }
  ): Promise<{ success: boolean; artifactId?: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/migrate/canvas`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async migrateDoc(
    accountId: string,
    projectId: string,
    data: {
      title: string
      tiptapContent: Record<string, unknown> | null
      plainText: string
      subType?: string
      domain?: ArtifactDomain
    }
  ): Promise<{ success: boolean; artifactId?: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/migrate/doc`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getMigrationStatus(
    accountId: string,
    projectId: string
  ): Promise<{ hasArtifacts: boolean; artifactCounts: Record<ArtifactTypeEnum, number> }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/migration-status`)
  },

  // Journey integration
  async linkToJourney(
    accountId: string,
    projectId: string,
    artifactId: string,
    journeyPhaseId: string | null,
    journeyActivityId: string | null
  ): Promise<{ artifact: Artifact }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/journey`, {
      method: 'PATCH',
      body: JSON.stringify({ journeyPhaseId, journeyActivityId }),
    })
  },

  async getForPhase(
    accountId: string,
    projectId: string,
    phaseId: string
  ): Promise<{ artifacts: Artifact[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/journey/phase/${phaseId}`)
  },

  async getForActivity(
    accountId: string,
    projectId: string,
    activityId: string
  ): Promise<{ artifacts: Artifact[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/journey/activity/${activityId}`)
  },
}

// ============================================================================
// CANVAS INTERACTIVE API (connections, story)
// ============================================================================

export const canvasInteractiveApi = {
  async getConnections(
    accountId: string,
    projectId: string,
    canvasId: string,
    templateId: string,
  ): Promise<{ relatedSections: Record<string, { target: string; label: string }[]> }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/artifacts/${canvasId}/interactive/connections?templateId=${templateId}`,
    )
  },

  async generateStory(
    accountId: string,
    projectId: string,
    canvasId: string,
    data: {
      canvasData: Record<string, string[]>
      projectName: string
      templateId: string
    },
  ): Promise<{ narrative: string; generatedAt: string }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/artifacts/${canvasId}/interactive/story`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    )
  },
}

// ============================================================================
// ARTIFACT TEMPLATES API (Account-scoped)
// ============================================================================

export const artifactTemplatesApi = {
  async list(accountId: string): Promise<{ templates: ArtifactTemplate[] }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates`)
  },

  async getTaxonomy(accountId: string): Promise<{ taxonomy: ArtifactTaxonomy }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/taxonomy`)
  },

  async get(accountId: string, templateId: string): Promise<{ template: ArtifactTemplate }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/${templateId}`)
  },

  async create(
    accountId: string,
    data: {
      key: string
      name: string
      description?: string
      type: ArtifactTypeEnum
      subType: string
      domain: ArtifactDomain
      structure: Record<string, unknown>
      defaultContent?: Record<string, unknown>
      icon?: string
      color?: string
    }
  ): Promise<{ template: ArtifactTemplate }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async update(
    accountId: string,
    templateId: string,
    data: {
      name?: string
      description?: string
      structure?: Record<string, unknown>
      defaultContent?: Record<string, unknown>
      icon?: string
      color?: string
      isActive?: boolean
      sortOrder?: number
    }
  ): Promise<{ template: ArtifactTemplate }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  async delete(accountId: string, templateId: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/${templateId}`, {
      method: 'DELETE',
    })
  },

  async initialize(accountId: string): Promise<{ templates: ArtifactTemplate[]; initialized: number }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/initialize`, {
      method: 'POST',
    })
  },
}

// ============================================================================
// DOC TEMPLATES API
// ============================================================================

/**
 * Doc Templates API - Template configurator for Smart Docs
 * Manage document templates with section configuration and entity shortcuts
 */
export const docTemplatesApi = {
  /**
   * List all doc templates for an account
   */
  async list(accountId: string): Promise<DocTemplatesListResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates`)
  },

  /**
   * Get a specific doc template with full configuration
   */
  async get(accountId: string, templateId: string): Promise<DocTemplateGetResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/${templateId}`)
  },

  /**
   * Create a new doc template
   */
  async create(
    accountId: string,
    data: CreateDocTemplateRequest
  ): Promise<DocTemplateGetResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /**
   * Update template configuration (sections + entity shortcuts)
   */
  async updateConfig(
    accountId: string,
    templateId: string,
    config: UpdateDocTemplateConfigRequest
  ): Promise<DocTemplateGetResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/${templateId}/config`, {
      method: 'PATCH',
      body: JSON.stringify(config),
    })
  },

  /**
   * Delete a doc template
   */
  async delete(accountId: string, templateId: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/${templateId}`, {
      method: 'DELETE',
    })
  },

  /**
   * Get configurable items for drag & drop (section templates + entities)
   */
  async getConfigurableItems(accountId: string): Promise<DocConfigurableItemsResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/configurable-items`)
  },

  /**
   * List section templates
   */
  async listSectionTemplates(accountId: string): Promise<SectionTemplatesListResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/section-templates`)
  },

  /**
   * Create a new section template
   */
  async createSectionTemplate(
    accountId: string,
    data: CreateSectionTemplateRequest
  ): Promise<{ section: SectionTemplate }> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/section-templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /**
   * Create a document from a template
   */
  async createDocFromTemplate(
    accountId: string,
    projectId: string,
    templateId: string,
    data: { title: string }
  ): Promise<{ doc: unknown }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/docs/from-template/${templateId}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },
}

// ============================================================================
// VALIDATION ANALYSIS API (Startup Validation Ladder)
// ============================================================================

export interface ValidationStageResponse {
  id: string
  key: string
  name: string
  question: string
  stageNumber: number
  signals: {
    key: string
    name: string
    description: string
    evidenceTypes: string[]
  }[]
  createdAt: string
}

export interface ValidationAnalysisResponse {
  id: string
  projectId: string
  status: 'pending' | 'gathering' | 'evaluating' | 'generating' | 'completed' | 'failed'
  currentStep: string | null
  currentStage: string | null
  validatedStages: number
  stageScores: Array<{
    stageKey: string
    stageName: string
    stageNumber: number
    question: string
    confidenceScore: number
    status: 'validated' | 'in_progress' | 'not_started'
    signals: Array<{
      signalKey: string
      signalName: string
      description: string
      isValidated: boolean
      evidenceCount: number
      evidenceSummary: string
    }>
    evidenceQuotes: Array<{
      quote: string
      sourceId: string
      sourceTitle: string
      sourceType: string
      relevance: number
    }>
    recommendations: Array<{
      title: string
      description: string
      priority: 'high' | 'medium' | 'low'
      category: string
      actionItems: string[]
    }>
  }>
  artifactId: string | null
  sourcesUsed: Array<{ id: string; type: string; title: string; usedInStages: string[] }>
  evidenceCount: number
  startedAt: string | null
  completedAt: string | null
  failedAt: string | null
  errorMessage: string | null
  createdById: string
  createdAt: string
  updatedAt: string
}

export interface ValidationStatusResponse {
  id: string
  status: string
  currentStep: string | null
  progress: {
    stepsCompleted: number
    totalSteps: number
    currentStepName: string | null
  }
  stageScores: ValidationAnalysisResponse['stageScores'] | null
}

export const validationAnalysisApi = {
  /** Get all validation stages (reference data) */
  async getStages(
    accountId: string,
    projectId: string
  ): Promise<{ stages: ValidationStageResponse[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation/stages`)
  },

  /** Get a single validation stage by key */
  async getStage(
    accountId: string,
    projectId: string,
    stageKey: string
  ): Promise<{ stage: ValidationStageResponse }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation/stages/${stageKey}`)
  },

  /** List all validation analyses for a project */
  async list(
    accountId: string,
    projectId: string
  ): Promise<{ analyses: ValidationAnalysisResponse[]; total: number }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation`)
  },

  /** Get the latest validation analysis */
  async getLatest(
    accountId: string,
    projectId: string
  ): Promise<{ analysis: ValidationAnalysisResponse }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation/latest`)
  },

  /** Get the latest completed validation analysis */
  async getLatestCompleted(
    accountId: string,
    projectId: string
  ): Promise<{ analysis: ValidationAnalysisResponse }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation/latest/completed`)
  },

  /** Get a specific validation analysis by ID */
  async get(
    accountId: string,
    projectId: string,
    analysisId: string
  ): Promise<{ analysis: ValidationAnalysisResponse }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation/${analysisId}`)
  },

  /** Get validation analysis status (for polling) */
  async getStatus(
    accountId: string,
    projectId: string,
    analysisId: string
  ): Promise<{ status: ValidationStatusResponse }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation/${analysisId}/status`)
  },

  /** Create a new validation analysis */
  async create(
    accountId: string,
    projectId: string
  ): Promise<{ analysis: ValidationAnalysisResponse }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation`, {
      method: 'POST',
    })
  },

  /** Run a pending validation analysis */
  async run(
    accountId: string,
    projectId: string,
    analysisId: string
  ): Promise<{ analysis: ValidationAnalysisResponse }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation/${analysisId}/run`, {
      method: 'POST',
    })
  },

  /** Delete a validation analysis */
  async delete(
    accountId: string,
    projectId: string,
    analysisId: string
  ): Promise<void> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/validation/${analysisId}`, {
      method: 'DELETE',
    })
  },
}

// ============================================================================
// DEDUPLICATION API
// ============================================================================

export interface DedupSourceInfo {
  uri: string
  title: string
}

export interface DedupCandidate {
  entityAUri: string
  entityALabel: string
  entityADescription: string
  entityBUri: string
  entityBLabel: string
  entityBDescription: string
  entityType: string
  domain: string
  similarityScore: number
  tier: 'exact' | 'similar'
  entityASources: DedupSourceInfo[]
  entityBSources: DedupSourceInfo[]
  entityAEvidence: string[]
  entityBEvidence: string[]
  entityAConfidence: number
  entityBConfidence: number
  entityASource: string
  entityBSource: string
  entityAStatus: string
  entityBStatus: string
}

export interface DedupStats {
  totalEntities: number
  duplicatesFound: number
  autoMerged: number
  pendingReview: number
}

export interface DedupEvent {
  id: string
  projectId: string
  canonicalUri: string
  deprecatedUri: string
  canonicalLabel: string
  deprecatedLabel: string
  entityType: string
  domain: string
  similarityScore: number
  tier: 'exact' | 'similar' | 'manual'
  action: 'merged' | 'split' | 'dismissed'
  reviewedById: string | null
  createdAt: string
}

export const dedupApi = {
  async getCandidates(
    accountId: string,
    projectId: string,
    domain?: string
  ): Promise<{ candidates: DedupCandidate[]; total: number }> {
    const query = domain ? `?domain=${domain}` : ''
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dedup/candidates${query}`)
  },

  async getStats(
    accountId: string,
    projectId: string
  ): Promise<DedupStats> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dedup/stats`)
  },

  async getEvents(
    accountId: string,
    projectId: string
  ): Promise<{ events: DedupEvent[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dedup/events`)
  },

  async runDedup(
    accountId: string,
    projectId: string,
    domain?: string
  ): Promise<{ autoMerged: number; candidatesFound: number }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dedup/run`, {
      method: 'POST',
      body: JSON.stringify({ domain }),
    })
  },

  async merge(
    accountId: string,
    projectId: string,
    canonicalUri: string,
    deprecatedUri: string
  ): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dedup/merge`, {
      method: 'POST',
      body: JSON.stringify({ canonicalUri, deprecatedUri }),
    })
  },

  async split(
    accountId: string,
    projectId: string,
    canonicalUri: string,
    deprecatedUri: string
  ): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dedup/split`, {
      method: 'POST',
      body: JSON.stringify({ canonicalUri, deprecatedUri }),
    })
  },

  async dismiss(
    accountId: string,
    projectId: string,
    entityAUri: string,
    entityBUri: string
  ): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dedup/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ entityAUri, entityBUri }),
    })
  },
}

// ============================================================================
// AI INTERVIEW API (authenticated, project-scoped)
// ============================================================================

export const aiInterviewApi = {
  async createSession(
    accountId: string,
    projectId: string,
    data: {
      researchGoal: string
      interviewType: string
      linkedPersonName: string
      focusTopics?: string[]
      linkedPersonId?: string
      title?: string
    },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/sessions`,
      { method: 'POST', body: JSON.stringify(data) },
    )
  },

  async listSessions(accountId: string, projectId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/sessions`,
    )
  },

  async getSession(accountId: string, projectId: string, sessionId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/sessions/${sessionId}`,
    )
  },

  async generatePlanStream(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: { apiKey: string },
    onEvent: (event: AIInterviewStreamEvent) => void,
  ): Promise<void> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = getSessionToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
    const response = await fetch(
      `${API_URL}/api/accounts/${accountId}/projects/${projectId}/ai-interview/sessions/${sessionId}/plan`,
      { method: 'POST', headers, body: JSON.stringify(data) },
    )
    if (!response.ok) throw new Error(`Plan generation failed: ${response.statusText}`)
    await parseSSEStream(response, onEvent)
  },

  async chatStream(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: {
      content: string
      apiKey: string
      inputMethod?: 'text' | 'voice' | 'mcq'
      selectedMcqOption?: string
    },
    onEvent: (event: AIInterviewStreamEvent) => void,
  ): Promise<void> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = getSessionToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
    const response = await fetch(
      `${API_URL}/api/accounts/${accountId}/projects/${projectId}/ai-interview/sessions/${sessionId}/messages`,
      { method: 'POST', headers, body: JSON.stringify(data) },
    )
    if (!response.ok) throw new Error(`Chat stream failed: ${response.statusText}`)
    await parseSSEStream(response, onEvent)
  },

  async completeSession(accountId: string, projectId: string, sessionId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/sessions/${sessionId}/complete`,
      { method: 'POST' },
    )
  },

  async getEntities(accountId: string, projectId: string, sessionId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/sessions/${sessionId}/entities`,
    )
  },
}

/** Shared SSE stream parser */
async function parseSSEStream(
  response: Response,
  onEvent: (event: AIInterviewStreamEvent) => void,
): Promise<void> {
  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''
  let currentEventType = ''

  const processLine = (line: string) => {
    if (line.startsWith('event:')) {
      currentEventType = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      try {
        const data = JSON.parse(line.slice(5).trim())
        onEvent({ type: currentEventType, ...data })
      } catch {
        // Ignore parse errors for incomplete chunks
      }
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''
    for (const line of lines) processLine(line)
  }
  if (buffer.trim()) processLine(buffer)
}

// ============================================================================
// AI INTERVIEW PLAN API (authenticated, project-scoped)
// ============================================================================

export const aiInterviewPlanApi = {
  async createPlan(
    accountId: string,
    projectId: string,
    data: {
      title: string
      researchGoal: string
      interviewType?: string
      focusTopics?: string[]
      description?: string
      welcomeMessage?: string
      consentText?: string
    },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans`,
      { method: 'POST', body: JSON.stringify(data) },
    )
  },

  async listPlans(accountId: string, projectId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans`,
    )
  },

  async getPlan(accountId: string, projectId: string, planId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans/${planId}`,
    )
  },

  async updatePlan(
    accountId: string,
    projectId: string,
    planId: string,
    data: Record<string, unknown>,
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans/${planId}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    )
  },

  async deletePlan(accountId: string, projectId: string, planId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans/${planId}`,
      { method: 'DELETE' },
    )
  },

  async generateQuestions(
    accountId: string,
    projectId: string,
    planId: string,
    data: { apiKey: string },
    onEvent: (event: AIInterviewStreamEvent) => void,
  ): Promise<void> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = getSessionToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
    const response = await fetch(
      `${API_URL}/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans/${planId}/generate`,
      { method: 'POST', headers, body: JSON.stringify(data) },
    )
    if (!response.ok) throw new Error(`Question generation failed: ${response.statusText}`)
    await parseSSEStream(response, onEvent)
  },

  async activatePlan(accountId: string, projectId: string, planId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans/${planId}/activate`,
      { method: 'POST' },
    )
  },

  async deactivatePlan(accountId: string, projectId: string, planId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans/${planId}/deactivate`,
      { method: 'POST' },
    )
  },

  async listSessions(accountId: string, projectId: string, planId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans/${planId}/sessions`,
    )
  },

  async regenerateLink(accountId: string, projectId: string, planId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/ai-interview/plans/${planId}/regenerate-link`,
      { method: 'POST' },
    )
  },
}

// ============================================================================
// PUBLIC INTERVIEW API (no auth — for share-link interviewees)
// ============================================================================

export const publicInterviewApi = {
  async getLanding(shareToken: string) {
    const response = await fetch(`${API_URL}/interview/${shareToken}`)
    if (!response.ok) throw new Error(`Failed to load interview: ${response.statusText}`)
    return response.json()
  },

  async startInterview(
    shareToken: string,
    data: { name?: string; email?: string; consentGiven: boolean },
  ) {
    const response = await fetch(`${API_URL}/interview/${shareToken}/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: response.statusText }))
      throw new Error(err.error || `Failed to start interview: ${response.statusText}`)
    }
    return response.json() as Promise<{ sessionId: string; sessionToken: string; openingMessage: string | null; mcqOptions: string[] | null }>
  },

  async sendMessage(
    shareToken: string,
    sessionId: string,
    sessionToken: string,
    data: { content: string; inputMethod?: string; selectedMcqOption?: string },
    onEvent: (event: AIInterviewStreamEvent) => void,
  ): Promise<void> {
    const response = await fetch(
      `${API_URL}/interview/${shareToken}/sessions/${sessionId}/messages`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Interview-Token': sessionToken,
        },
        body: JSON.stringify(data),
      },
    )
    if (!response.ok) throw new Error(`Message failed: ${response.statusText}`)
    await parseSSEStream(response, onEvent)
  },

  async completeInterview(shareToken: string, sessionId: string, sessionToken: string) {
    const response = await fetch(
      `${API_URL}/interview/${shareToken}/sessions/${sessionId}/complete`,
      {
        method: 'POST',
        headers: { 'X-Interview-Token': sessionToken },
      },
    )
    if (!response.ok) throw new Error(`Failed to complete interview: ${response.statusText}`)
    return response.json()
  },
}

// ============================================================================
// PERSONA WORKSHOP API
// ============================================================================

export const personaWorkshopApi = {
  async createSession(
    accountId: string,
    projectId: string,
    data: { personaType: 'user' | 'buyer'; mode: 'quick' | 'deep' },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions`,
      { method: 'POST', body: JSON.stringify(data) },
    )
  },

  async listSessions(accountId: string, projectId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions`,
    )
  },

  async getSession(accountId: string, projectId: string, sessionId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/${sessionId}`,
    )
  },

  async updateSession(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: { currentPhase?: number; personaData?: Record<string, unknown>; personaType?: 'user' | 'buyer' },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/${sessionId}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    )
  },

  async suggest(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: { phaseId: string; apiKey: string },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/${sessionId}/suggest`,
      { method: 'POST', body: JSON.stringify(data) },
    )
  },

  async complete(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: { apiKey?: string },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/${sessionId}/complete`,
      { method: 'POST', body: JSON.stringify(data) },
    )
  },

  async deleteSession(accountId: string, projectId: string, sessionId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/${sessionId}`,
      { method: 'DELETE' },
    )
  },

  async syncSession(accountId: string, projectId: string, sessionId: string) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/${sessionId}/sync`,
      { method: 'POST' },
    )
  },

  async createSessionFromEntity(
    accountId: string,
    projectId: string,
    data: { entityUri: string; label: string; personaType?: 'user' | 'buyer' },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/from-entity`,
      { method: 'POST', body: JSON.stringify(data) },
    )
  },

  async suggestFromIntelligence(
    accountId: string,
    projectId: string,
    data: { personaType: 'user' | 'buyer'; apiKey: string },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/suggest-from-intelligence`,
      { method: 'POST', body: JSON.stringify(data) },
    )
  },

  async synthesizeProblem(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: { jtbdId: string; apiKey?: string },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/${sessionId}/synthesize-problem`,
      { method: 'POST', body: JSON.stringify(data) },
    )
  },

  async createFromDocument(
    accountId: string,
    projectId: string,
    data: { content: string; personaType?: 'user' | 'buyer'; apiKey?: string },
  ) {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/persona-workshop/sessions/from-document`,
      { method: 'POST', body: JSON.stringify(data) },
    )
  },
}

// Stub APIs for features in progress on this branch
export const competitorMapApi = {
  async listCompetitors(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/competitors`)
  },
  async createCompetitor(accountId: string, projectId: string, data: any): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/competitors`, { method: 'POST', body: JSON.stringify(data) })
  },
  async updateCompetitor(accountId: string, projectId: string, id: string, data: any): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/competitors/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  },
  async deleteCompetitor(accountId: string, projectId: string, id: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/competitors/${id}`, { method: 'DELETE' })
  },
  async getColumnSettings(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/column-settings`)
  },
  async upsertColumnSettings(accountId: string, projectId: string, data: any): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/column-settings`, { method: 'PATCH', body: JSON.stringify(data) })
  },
  async updateColumnSettings(accountId: string, projectId: string, data: any): Promise<any> {
    return this.upsertColumnSettings(accountId, projectId, data)
  },
  async scrapeUrl(accountId: string, projectId: string, data: { url: string; apiKey: string }): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/scrape`, { method: 'POST', body: JSON.stringify(data) })
  },
  async enrichCompetitor(accountId: string, projectId: string, id: string, apiKey: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/competitors/${id}/enrich`, { method: 'POST', body: JSON.stringify({ apiKey }) })
  },
  async generateBrief(accountId: string, projectId: string, data: any): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/briefs/generate`, { method: 'POST', body: JSON.stringify(data) })
  },
  async getBriefByCompetitor(accountId: string, projectId: string, competitorId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/briefs/competitor/${competitorId}`)
  },
  async getBriefByCategory(accountId: string, projectId: string, category: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/briefs/category/${encodeURIComponent(category)}`)
  },
  async discoverCompetitors(accountId: string, projectId: string, data: { apiKey: string }): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/competitor-map/discover`, { method: 'POST', body: JSON.stringify(data) })
  },
}

export const marketSegmentationApi = {
  async getCanvas(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/canvas`)
  },
  async updateWeights(accountId: string, projectId: string, weights: Record<string, number>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/canvas/weights`, {
      method: 'PATCH', body: JSON.stringify({ weights }),
    })
  },
  async setBeachhead(accountId: string, projectId: string, segmentId: string | null): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/canvas/beachhead`, {
      method: 'PATCH', body: JSON.stringify({ segmentId }),
    })
  },
  async createSegment(accountId: string, projectId: string, data: Record<string, unknown>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },
  async updateSegment(accountId: string, projectId: string, id: string, data: Record<string, unknown>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    })
  },
  async deleteSegment(accountId: string, projectId: string, id: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${id}`, {
      method: 'DELETE',
    })
  },
  async reorderSegments(accountId: string, projectId: string, orderedIds: string[]): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/reorder`, {
      method: 'PATCH', body: JSON.stringify({ orderedIds }),
    })
  },
  async updateField(
    accountId: string, projectId: string, segmentId: string,
    path: 'sizing' | 'scoring', key: string, value: number | null,
  ): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${segmentId}/field`, {
      method: 'PATCH', body: JSON.stringify({ path, key, value }),
    })
  },
  async resetField(
    accountId: string, projectId: string, segmentId: string,
    path: 'sizing' | 'scoring', key: string,
  ): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${segmentId}/reset-field`, {
      method: 'POST', body: JSON.stringify({ path, key }),
    })
  },
  async generateScoring(accountId: string, projectId: string, segmentId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${segmentId}/generate-scoring`, {
      method: 'POST',
    })
  },
  async generateSizing(accountId: string, projectId: string, segmentId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${segmentId}/generate-sizing`, {
      method: 'POST',
    })
  },
  async generateBrief(accountId: string, projectId: string, segmentId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${segmentId}/generate-brief`, {
      method: 'POST',
    })
  },
  async listBriefs(accountId: string, projectId: string, segmentId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${segmentId}/briefs`)
  },
  async createPersona(accountId: string, projectId: string, data: Record<string, unknown>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/personas`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },
  async updatePersona(accountId: string, projectId: string, id: string, data: Record<string, unknown>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/personas/${id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    })
  },
  async deletePersona(accountId: string, projectId: string, id: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/personas/${id}`, {
      method: 'DELETE',
    })
  },
  async linkPersona(
    accountId: string, projectId: string, segmentId: string,
    personaId: string, coverage: string, notes?: string | null,
  ): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${segmentId}/personas`, {
      method: 'POST', body: JSON.stringify({ personaId, coverage, notes }),
    })
  },
  async unlinkPersona(accountId: string, projectId: string, segmentId: string, personaId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/market-segmentation/segments/${segmentId}/personas/${personaId}`, {
      method: 'DELETE',
    })
  },
}

export const businessModelApi = {
  async ensureDefault(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/business-model/ensure-default`, { method: 'POST' })
  },
  async listCanvases(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/business-model`)
  },
  async createCanvas(accountId: string, projectId: string, data: { name: string; description?: string }): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/business-model`, { method: 'POST', body: JSON.stringify(data) })
  },
  async getCanvas(accountId: string, projectId: string, canvasId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/business-model/${canvasId}`)
  },
  async updateCanvas(accountId: string, projectId: string, canvasId: string, data: any): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/business-model/${canvasId}`, { method: 'PATCH', body: JSON.stringify(data) })
  },
  async deleteCanvas(accountId: string, projectId: string, canvasId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/business-model/${canvasId}`, { method: 'DELETE' })
  },
  async generateCanvas(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/business-model/generate`, { method: 'POST' })
  },
}

export const solutionRequirementsApi = {
  // Tree & setup
  async getTree(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/tree`)
  },
  async ensureDefault(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/ensure-default`, { method: 'POST' })
  },

  // Generation
  async generate(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/generate`, { method: 'POST' })
  },
  async generateVision(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/generate/vision`, { method: 'POST' })
  },
  async generatePrd(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/generate/prd`, { method: 'POST' })
  },
  async generateSpecs(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/generate/specs`, { method: 'POST' })
  },

  // Vision CRUD
  async createVision(accountId: string, projectId: string, data: { name: string; visionStatement?: string }): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/visions`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },
  async updateVision(accountId: string, projectId: string, visionId: string, data: Record<string, unknown>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/visions/${visionId}`, {
      method: 'PATCH', body: JSON.stringify(data),
    })
  },

  // Capability CRUD
  async createCapability(accountId: string, projectId: string, visionId: string, data: { name: string; description?: string }): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/visions/${visionId}/capabilities`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },
  async updateCapability(accountId: string, projectId: string, capabilityId: string, data: Record<string, unknown>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/capabilities/${capabilityId}`, {
      method: 'PATCH', body: JSON.stringify(data),
    })
  },

  // Feature CRUD
  async createFeature(accountId: string, projectId: string, capabilityId: string, data: Record<string, unknown>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/capabilities/${capabilityId}/features`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },
  async updateFeature(accountId: string, projectId: string, featureId: string, data: Record<string, unknown>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/features/${featureId}`, {
      method: 'PATCH', body: JSON.stringify(data),
    })
  },
  async deleteFeature(accountId: string, projectId: string, featureId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/features/${featureId}`, {
      method: 'DELETE',
    })
  },

  // Evidence
  async linkEvidence(accountId: string, projectId: string, featureId: string, data: { type: string; uri: string; label: string; severity?: string; confidence?: number }): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/features/${featureId}/evidence`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },

  // Export
  async exportMarkdown(accountId: string, projectId: string, tier: string): Promise<string> {
    const res = await fetch(`/api/accounts/${accountId}/projects/${projectId}/solution-requirements/export/${tier}`, {
      credentials: 'include',
    })
    return res.text()
  },
}

export const valueDesignApi = {
  async list(accountId: string, projectId: string): Promise<any[]> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/value-design`)
  },
  async get(accountId: string, projectId: string, canvasId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/value-design/${canvasId}`)
  },
  async create(accountId: string, projectId: string, data: { personaSessionId: string; jtbdId: string }): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/value-design`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },
  async update(accountId: string, projectId: string, canvasId: string, data: Record<string, unknown>): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/value-design/${canvasId}`, {
      method: 'PATCH', body: JSON.stringify(data),
    })
  },
  async delete(accountId: string, projectId: string, canvasId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/value-design/${canvasId}`, {
      method: 'DELETE',
    })
  },
  async aiDraft(accountId: string, projectId: string, canvasId: string, block: string, apiKey: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/value-design/${canvasId}/ai-draft`, {
      method: 'POST', body: JSON.stringify({ block, apiKey }),
    })
  },
}

export const solutionWorkspaceApi = {
  async ensureDefault(
    accountId: string,
    projectId: string
  ): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-workspace/ensure-default`, {
      method: 'POST',
    })
  },

  async listActivities(
    accountId: string,
    projectId: string
  ): Promise<WorkspaceActivity[]> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-workspace/activities`)
  },

  async getDocumentByActivity(
    accountId: string,
    projectId: string,
    activityId: string
  ): Promise<unknown> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-workspace/activities/${activityId}/documents`)
  },

  async createDocument(
    accountId: string,
    projectId: string,
    activityId: string,
    title: string
  ): Promise<unknown> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-workspace/activities/${activityId}/documents`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    })
  },

  async listBlocks(
    accountId: string,
    projectId: string,
    documentId: string
  ): Promise<WorkspaceDocBlock[]> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/solution-workspace/documents/${documentId}/blocks`
    )
  },

  async createBlock(
    accountId: string,
    projectId: string,
    documentId: string,
    data: {
      parentBlockId?: string
      blockType: string
      content: string
      richContent?: Record<string, unknown>
      sortOrder: number
      depth: number
    }
  ): Promise<WorkspaceDocBlock> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/solution-workspace/documents/${documentId}/blocks`,
      { method: 'POST', body: JSON.stringify(data) }
    )
  },

  async updateBlock(
    accountId: string,
    projectId: string,
    blockId: string,
    documentId: string,
    data: Partial<{
      content: string
      richContent: Record<string, unknown>
      sortOrder: number
      metadata: Record<string, unknown>
    }>
  ): Promise<WorkspaceDocBlock> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/solution-workspace/blocks/${blockId}?documentId=${documentId}`,
      { method: 'PATCH', body: JSON.stringify(data) }
    )
  },

  async deleteBlock(
    accountId: string,
    projectId: string,
    blockId: string,
    documentId: string
  ): Promise<void> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/solution-workspace/blocks/${blockId}?documentId=${documentId}`,
      { method: 'DELETE' }
    )
  },

  async generateVisionStream(
    accountId: string,
    projectId: string,
    activityId: string,
    options?: { apiKey?: string },
    onEvent?: (event: Record<string, unknown>) => void,
  ): Promise<Response> {
    const token = getSessionToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    const response = await fetch(`/api/accounts/${accountId}/projects/${projectId}/solution-workspace/activities/${activityId}/generate`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ apiKey: options?.apiKey || '' }),
    })

    if (onEvent && response.ok && response.body) {
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let currentEventType = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim()
            continue
          }
          if (!line.startsWith('data: ')) continue
          const jsonStr = line.slice(6).trim()
          if (!jsonStr || jsonStr === '[DONE]') continue
          try {
            const parsed = JSON.parse(jsonStr)
            // Hono streamSSE writes event type on 'event:' line and data on 'data:' line
            const eventType = currentEventType || parsed.type || ''
            onEvent({ type: eventType, ...parsed })
            currentEventType = ''
          } catch {
            // Skip malformed SSE lines
          }
        }
      }
    }

    return response
  },

  async regenerateSection(
    accountId: string,
    projectId: string,
    blockId: string,
    options: { documentId: string; apiKey?: string },
  ): Promise<{ draft: DraftSuggestion }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-workspace/blocks/${blockId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ documentId: options.documentId, apiKey: options.apiKey || '' }),
    })
  },

  async sendChatMessage(
    accountId: string,
    projectId: string,
    activityId: string,
    body: { message: string; documentId: string; apiKey: string },
    signal?: AbortSignal
  ): Promise<Response> {
    const token = getSessionToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    return fetch(`/api/accounts/${accountId}/projects/${projectId}/solution-workspace/activities/${activityId}/chat`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
      signal,
    })
  },

  async fetchChatHistory(
    accountId: string,
    projectId: string,
    activityId: string
  ): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/solution-workspace/activities/${activityId}/chat/history`)
  },
}

// ============================================================================
// DUAL-SPACE (Problem-Solution Map)
// ============================================================================

export const dualSpaceApi = {
  async getData(accountId: string, projectId: string, filters?: {
    personaSessionId?: string; jtbdId?: string
  }) {
    const params = new URLSearchParams()
    if (filters?.personaSessionId) params.set('personaSessionId', filters.personaSessionId)
    if (filters?.jtbdId) params.set('jtbdId', filters.jtbdId)
    const qs = params.toString()
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space${qs ? `?${qs}` : ''}`)
  },

  async listLinks(accountId: string, projectId: string) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/cross-links`)
  },

  async createLink(accountId: string, projectId: string, data: {
    sourceType: string; sourceId: string; targetType: string; targetId: string
    relation?: string; rationale?: string
  }) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/cross-links`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },

  async deleteLink(accountId: string, projectId: string, linkId: string) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/cross-links/${linkId}`, {
      method: 'DELETE',
    })
  },

  async listNeeds(accountId: string, projectId: string) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/needs`)
  },

  async createNeed(accountId: string, projectId: string, data: {
    personaSessionId: string; jtbdId: string; statement: string
    contributingPainIds?: string[]; contributingGainIds?: string[]
  }) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/needs`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },

  async updateNeed(accountId: string, projectId: string, needId: string, data: Record<string, unknown>) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/needs/${needId}`, {
      method: 'PATCH', body: JSON.stringify(data),
    })
  },

  async deleteNeed(accountId: string, projectId: string, needId: string) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/needs/${needId}`, {
      method: 'DELETE',
    })
  },

  async getGaps(accountId: string, projectId: string) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/gaps`)
  },

  async getValueProp(accountId: string, projectId: string, filters?: {
    personaSessionId?: string; jtbdId?: string
  }) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/value-prop`, {
      method: 'POST', body: JSON.stringify(filters || {}),
    })
  },

  async createBenefit(accountId: string, projectId: string, data: {
    featureId: string; statement: string; linkedGainIds?: string[]
  }) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/benefits`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },

  async deleteBenefit(accountId: string, projectId: string, benefitId: string) {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/benefits/${benefitId}`, {
      method: 'DELETE',
    })
  },

  /** Stream coach SSE events */
  async coachStream(accountId: string, projectId: string, data: {
    message: string; apiKey: string
    selectedNodeId?: string; selectedNodeType?: string; selectedNodeTitle?: string
  }): Promise<Response> {
    const token = localStorage.getItem('ispot-session-token')
    return fetch(`/api/accounts/${accountId}/projects/${projectId}/dual-space/coach/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    })
  },
}

// ============================================================================
// SKERPA COPILOT CHAT
// ============================================================================

export const skerpaChatApi = {
  async sendMessage(
    body: {
      message: string
      systemContext: string
      history?: Array<{ role: string; content: string }>
      mode?: string
      // Optional — when both are set the backend loads this account's Composio
      // tool catalog and exposes the tools to the LLM.
      accountId?: string
      userId?: string
    },
    signal?: AbortSignal,
  ): Promise<Response> {
    const token = localStorage.getItem('ispot-session-token')
    return fetch('/api/skerpa/chat/stream', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    })
  },

  async checkHealth(): Promise<{ available: boolean }> {
    return apiFetch('/api/skerpa/health')
  },
}

// ============================================================================
// REQUIREMENTS API (Hierarchical PRD Builder)
// ============================================================================

export const requirementsApi = {
  async list(accountId: string, projectId: string): Promise<any[]> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements`)
  },
  async create(accountId: string, projectId: string, data: any): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements`, { method: 'POST', body: JSON.stringify(data) })
  },
  async get(accountId: string, projectId: string, id: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/${id}`)
  },
  async update(accountId: string, projectId: string, id: string, data: any): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
  },
  async delete(accountId: string, projectId: string, id: string): Promise<void> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/${id}`, { method: 'DELETE' })
  },
  async reorder(accountId: string, projectId: string, data: { items: Array<{ id: string; sortOrder: number }> }): Promise<void> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/reorder`, { method: 'POST', body: JSON.stringify(data) })
  },
  async bulkUpdateStatus(accountId: string, projectId: string, data: { ids: string[]; status: string }): Promise<void> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/bulk-status`, { method: 'POST', body: JSON.stringify(data) })
  },
  async getSummary(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/summary`)
  },
  async getColumnSettings(accountId: string, projectId: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/column-settings`)
  },
  async upsertColumnSettings(accountId: string, projectId: string, data: { visibleColumns: Record<string, boolean> }): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/column-settings`, { method: 'PATCH', body: JSON.stringify(data) })
  },
  async bulkDelete(accountId: string, projectId: string, data: { ids: string[] }): Promise<void> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/bulk-delete`, { method: 'POST', body: JSON.stringify(data) })
  },
  async bulkUpdatePriority(accountId: string, projectId: string, data: { ids: string[]; priority: string | null }): Promise<void> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/bulk-priority`, { method: 'POST', body: JSON.stringify(data) })
  },
  async generateChildren(accountId: string, projectId: string, data: { parentId: string; apiKey?: string }): Promise<{ count: number }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/generate-children`, { method: 'POST', body: JSON.stringify(data) })
  },
  async generate(accountId: string, projectId: string, data: { apiKey: string }): Promise<{ count: number }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/generate`, { method: 'POST', body: JSON.stringify(data) })
  },
  async listThemes(accountId: string, projectId: string): Promise<{ themes: any[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/themes`)
  },
  async listTemplates(accountId: string, projectId: string): Promise<{ templates: any[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/templates`)
  },
  async createTheme(
    accountId: string, projectId: string,
    data: { name: string; color?: string | null; description?: string | null; ownerUserId?: string | null },
  ): Promise<{ theme: any }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/themes`, {
      method: 'POST', body: JSON.stringify(data),
    })
  },
  async updateTheme(
    accountId: string, projectId: string, themeId: string,
    data: { name?: string; color?: string | null; description?: string | null; sortOrder?: number },
  ): Promise<{ theme: any }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/themes/${themeId}`, {
      method: 'PATCH', body: JSON.stringify(data),
    })
  },
  async deleteTheme(accountId: string, projectId: string, themeId: string): Promise<void> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/themes/${themeId}`, { method: 'DELETE' })
  },
  async setThemeLinks(
    accountId: string, projectId: string, requirementId: string,
    themes: Array<{ themeId: string; isPrimary?: boolean }>,
  ): Promise<void> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/${requirementId}/themes`, {
      method: 'PUT', body: JSON.stringify({ themes }),
    })
  },
  async setPersonaLinks(
    accountId: string, projectId: string, requirementId: string,
    refs: string[] | Array<{ personaSessionId: string; isPrimary?: boolean }>,
  ): Promise<void> {
    // Legacy string[] is coerced to canonical shape with isPrimary=false.
    const personas = refs.map(r => typeof r === 'string'
      ? { personaSessionId: r, isPrimary: false }
      : { personaSessionId: r.personaSessionId, isPrimary: !!r.isPrimary })
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/${requirementId}/personas`, {
      method: 'PUT', body: JSON.stringify({ personas }),
    })
  },
  async setPrimaryPersona(
    accountId: string, projectId: string, requirementId: string,
    personaSessionId: string | null,
  ): Promise<void> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/${requirementId}/primary-persona`, {
      method: 'POST', body: JSON.stringify({ personaSessionId }),
    })
  },
  async getEffectivePersonas(
    accountId: string, projectId: string, requirementId: string,
  ): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/requirements/${requirementId}/effective-personas`)
  },
}

// ============================================================================
// COMPOSIO API
// ============================================================================

export interface ComposioApp {
  // Composio SDK shape varies; keep it permissive in the frontend.
  name?: string
  key?: string
  appId?: string
  displayName?: string
  description?: string
  logo?: string
  categories?: string[]
  [key: string]: unknown
}

export interface ComposioAction {
  name?: string
  displayName?: string
  description?: string
  [key: string]: unknown
}

export interface ComposioIntegration {
  id: string
  accountId: string
  userId: string
  appId: string
  connectionId: string
  scope: string | null
  displayName: string | null
  metadata: Record<string, unknown> | null
  isActive: boolean | null
  lastSyncedAt: string | null
  lastErrorAt: string | null
  lastErrorMessage: string | null
  createdAt: string
  updatedAt: string
}

export interface ComposioExecution {
  id: string
  integrationId: string
  appId: string
  actionName: string
  status: 'pending' | 'in_progress' | 'success' | 'failed' | null
  error: string | null
  source: 'manual' | 'agent' | 'workflow' | 'api' | null
  startedAt: string
  completedAt: string | null
  durationMs: number | null
  createdAt: string
}

type ComposioEnvelope<T> = { success: boolean; data?: T; error?: string }

async function composioFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const env = await apiFetch<ComposioEnvelope<T>>(`/api/composio${path}`, options)
  if (!env.success) {
    throw new Error(env.error || 'Composio request failed')
  }
  return env.data as T
}

export const composioApi = {
  /**
   * Start an OAuth flow for a toolkit.
   *
   * Backend calls `composio.toolkits.authorize(userId, slug)` and returns
   * the real provider consent URL plus a Composio-generated `connectionId`
   * that the frontend should poll with `finalizeOAuth` once the user has
   * completed auth in the other tab.
   */
  async getOAuthUrl(
    appId: string,
  ): Promise<{ url: string; connectionId: string; status: string }> {
    return composioFetch<{ url: string; connectionId: string; status: string }>(
      `/auth/url?appId=${encodeURIComponent(appId)}`,
    )
  },

  /**
   * Check Composio connection status and promote pending integration to active.
   * Poll this until `active === true` after the user authorizes.
   */
  async finalizeOAuth(
    connectionId: string,
  ): Promise<{
    integration: ComposioIntegration
    active: boolean
    status: string
  }> {
    return composioFetch<{
      integration: ComposioIntegration
      active: boolean
      status: string
    }>('/auth/finalize', {
      method: 'POST',
      body: JSON.stringify({ connectionId }),
    })
  },

  async listApps(): Promise<ComposioApp[]> {
    return composioFetch<ComposioApp[]>('/apps')
  },

  async listAppActions(appId: string): Promise<ComposioAction[]> {
    return composioFetch<ComposioAction[]>(`/apps/${encodeURIComponent(appId)}/actions`)
  },

  async listConnections(): Promise<ComposioIntegration[]> {
    return composioFetch<ComposioIntegration[]>('/connections')
  },

  async getConnection(id: string): Promise<ComposioIntegration> {
    return composioFetch<ComposioIntegration>(`/connections/${id}`)
  },

  async createConnection(payload: {
    appId: string
    connectionId: string
    accessToken: string
    refreshToken?: string
    scope?: string
    displayName?: string
    metadata?: Record<string, unknown>
  }): Promise<ComposioIntegration> {
    return composioFetch<ComposioIntegration>('/connections', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async deleteConnection(id: string): Promise<void> {
    await composioFetch<{ id: string }>(`/connections/${id}`, { method: 'DELETE' })
  },

  async listExecutions(
    integrationId: string,
    limit = 50,
    offset = 0,
  ): Promise<ComposioExecution[]> {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    return composioFetch<ComposioExecution[]>(
      `/executions/${integrationId}?${qs.toString()}`,
    )
  },
}


// ============================================================================
// FIT THESIS API
// ============================================================================

export const fitThesisApi = {
  async listTheses(accountId: string, projectId: string): Promise<{ theses: any[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/fit-theses/theses`)
  },
  async getThesis(accountId: string, projectId: string, id: string): Promise<{ thesis: any }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/fit-theses/theses/${id}`)
  },
  async createThesis(accountId: string, projectId: string, data: { personaSessionId: string; jtbdId: string; painId?: string }): Promise<{ thesis: any }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/fit-theses/theses`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  },
  async deleteThesis(accountId: string, projectId: string, id: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/fit-theses/theses/${id}`, { method: "DELETE" })
  },
  async validateThesis(accountId: string, projectId: string, id: string): Promise<{ thesis: any }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/fit-theses/theses/${id}/validate`, {
      method: "POST",
    })
  },
  async listReferenceLabels(accountId: string, projectId: string): Promise<{
    competitors: Array<{ id: string; label: string }>
    flaws: Array<{ id: string; label: string }>
    capabilities: Array<{ id: string; label: string }>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/fit-theses/reference-labels`)
  },
  async patchBlock(accountId: string, projectId: string, id: string, blockKey: string, value: unknown): Promise<{ thesis: any }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/fit-theses/theses/${id}/blocks/${blockKey}`, {
      method: "PATCH",
      body: JSON.stringify({ value }),
    })
  },
  async listChatMessages(accountId: string, projectId: string, id: string): Promise<{ messages: any[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/fit-theses/theses/${id}/chat`)
  },
  chatStreamUrl(accountId: string, projectId: string, id: string): string {
    return `${API_URL}/api/accounts/${accountId}/projects/${projectId}/fit-theses/theses/${id}/chat`
  },
}

export const guidanceApi = {
  async list(accountId: string, projectId: string): Promise<{ items: import('@ispot/shared').SkerpaGuidanceItem[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/guidance`)
  },
  async dismiss(accountId: string, projectId: string, id: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/guidance/${id}/dismiss`, { method: 'POST' })
  },
  async complete(accountId: string, projectId: string, id: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/guidance/${id}/complete`, { method: 'POST' })
  },
}

